// Deals workspace — Attio-style: tabs across the top, segmented view switcher (Table / Kanban),
// dense rich rows with inline pills, owner avatars, on-hold/exit badges.
//
// Data shape comes from /api/engagements (adapter in data.jsx). Stages are
// loaded from /api/pipeline/stages so the Kanban column order + colors track
// the canonical pipeline.

const STAGE_HUE = {
  prospect:            'slate',
  qualified:           'slate',
  interested:          'teal',
  ds_call:             'teal',
  hot_lead:            'teal',
  blast:               'violet',
  bs_call:             'violet',
  data_gathering:      'amber',
  valuation_presented: 'amber',
  loi:                 'blue',
  won:                 'green',
  // Formal sell-side process (082) — hues mirror the equivalent buyside step.
  ss_prospect:          'slate',
  ss_qualified:         'slate',
  ss_interested:        'teal',
  ss_valuation:         'amber',
  ss_pitch:             'teal',
  ss_cim_prep:          'amber',
  ss_buyer_outreach:    'violet',
  ss_ioi:               'blue',
  ss_loi:               'blue',
  ss_mgmt_presentation: 'blue',
  ss_won:               'green',
};

const fmtPct = (p) => p == null ? '—' : `${Number(p).toFixed(0)}%`;

// Per-column extractor map driving header-click sort. Stage uses pipeline
// ORDER (not slug alphabetically) so the funnel reads top-to-bottom.
// Keys here MUST match the col.key strings rendered in DealsTable below;
// SortableHeader uses presence in this map to decide whether a header is
// clickable.
// Keys the API can ORDER BY (mirrors _SORTABLE in routes/engagements.py).
// Anything outside this set — e.g. user-defined formula columns — still
// sorts in the client, i.e. across the loaded pages only. The Sort chip
// says which of the two you're looking at. `interactions` replaced the old
// client-only `notes` count: it's the denormalized tally (migration 089),
// so it sorts server-side like any real column.
const SERVER_SORT_KEYS = new Set([
  'name', 'stage', 'progress', 'fees', 'wfees', 'rev', 'ebitda',
  'sector', 'owner', 'estClose', 'updated', 'interactions',
  // Opt-in columns backed by plain engagement columns — see _SORTABLE in
  // routes/engagements.py. Keys must match that registry exactly; anything
  // missing here still works, it just sorts the loaded pages instead of the
  // whole tab (and the Sort chip says which you got).
  'type', 'dcStage', 'onHoldReason', 'associate',
  'closedDate', 'closingProcessDate', 'feesInvoiced', 'feesReceived',
  // Company lookup, like `sector` above — it reuses the same companies join.
  'website',
  // Hot score (202609161000) — a plain engagement column.
  'hotScore',
  // Soonest upcoming calendar event — joined per company (_NEXT_MEETING).
  'nextMeeting',
]);

// Per-tab default sort. Active reads as a working pipeline — furthest-along
// first (stage desc: LOI → Valuation Presented → Data Gathering → …); the
// other tabs keep most-recently-updated first. A manual header click, or a
// deep link that names its own sort, overrides this and then sticks across
// tab switches (userSortedRef below).
const TAB_DEFAULT_SORT = (t) => (
  t === 'active' ? { key: 'stage', dir: 'desc' } : { key: 'updated', dir: 'desc' }
);

// Where a stage sits in the pipeline: progress (probability_pct) first, then
// order_index. The sell-side family is numbered 21–31 against the classic
// funnel's 1–11, so order_index alone ranked a sell-side Qualified (5%) above
// a classic LOI (85%). Mirrors _SORTABLE's `stage` in routes/engagements.py.
const stageRank = (slug, stageBySlug) => {
  const s = stageBySlug[slug];
  return s ? (s.probability_pct ?? 0) * 1000 + (s.order_index ?? 0) : 1e9;
};

const SORT_EXTRACT = {
  name:     (d) => (d.name || '').toLowerCase(),
  stage:    (d, stageBySlug) => stageRank(d.stage, stageBySlug),
  // Progress = stage probability_pct (0-100), already on the row. Sorting by
  // it gives a "deals closest to closing" view independent of which exact
  // stage they're in.
  progress: (d) => d.probability,
  fees:     (d) => d.size,
  wfees:    (d) => d.weighted,
  // Match the column on screen (and the server's _SORTABLE): EstMoneyCell
  // falls back to the company estimate, so the sort has to see it too —
  // otherwise every "~" row ranks as a blank while showing a real number.
  rev:      (d) => d.reportedRevenue ?? d.companyRevenueEst,
  ebitda:   (d) => d.reportedEbitda ?? d.companyEbitdaEst,
  sector:   (d) => (d.sector || '').toLowerCase(),
  // Lead source (142) — a lookup name; client-side, like `contact` below.
  leadSource: (d) => (d.leadSource === '—' ? '' : (d.leadSource || '').toLowerCase()),
  website:  (d) => (d.companyDomain || '').toLowerCase(),
  owner:    (d) => (d.owner || '').toLowerCase(),
  // Client-side, like `contact` below: deal_team is a uuid[] and ordering by
  // the resolved names server-side would mean a join per row. Alphabetical by
  // the first member listed, which is how the column reads.
  dealTeam: (d) => ((d.dealTeamNames || [])[0] || '').toLowerCase(),
  // Client-side only, like `notes` was: ordering by the derived primary
  // contact server-side would be a correlated subquery over contacts
  // (~300k rows) in the ORDER BY — the exact shape _SORTABLE's header
  // comment rules out. The Sort chip already says client vs server.
  contact:  (d) => (d.contactName || '').toLowerCase(),
  contactEmail: (d) => (d.contactEmail || '').toLowerCase(),
  contactPhone: (d) => (d.contactPhone || '').toLowerCase(),
  estClose: (d) => d.estCloseDate || null,
  // Client-side sorts: next action by its due date — the note text has no
  // meaningful order — and latest note by recency.
  nextAction: (d) => d._raw?.next_action_date || null,
  latestNote: (d) => d.latestNoteAt || null,
  nextMeeting: (d) => d.nextMeetingAt || null,
  interactions: (d) => d.interactions || 0,
  // Prefer last_stage_update — that's when the deal actually moved, which is
  // what "Updated" means in an Inbox sense. updated_at gets touched on every
  // signal recompute / minor edit, so sorting by it puts noise at the top.
  // The displayed Updated column already uses last_stage_update for its time
  // string, so this also keeps the sort consistent with what's shown.
  updated:  (d) => d._raw?.last_stage_update || d._raw?.updated_at || d._raw?.last_synced_at || '',
  // When DealCloud created the engagement record (dc_created_at). Distinct
  // from Updated above, which tracks the last stage MOVE — that stays the
  // more useful "updated" for a deal, so this column only adds Created.
  created:  (d) => window.recordCreated(d._raw) || '',
  // Opt-in columns (ENGAGEMENT_COLUMNS, def:false). Client-side over the
  // loaded pages only — none of these are in SERVER_SORT_KEYS.
  type:         (d) => (d.type || '').toLowerCase(),
  dcStage:      (d) => (d.dcStage || '').toLowerCase(),
  onHoldReason: (d) => (d.onHoldReason || '').toLowerCase(),
  associate:    (d) => (d.associate || '').toLowerCase(),
  buyerCount:   (d) => d.buyerCount || 0,
  noteCount:    (d) => d.noteCount || 0,
  liveDate:     (d) => d.liveDate || '',
  closedDate:   (d) => d.closedDate || '',
  closingProcessDate: (d) => d.closingProcessDate || '',
  // Hot score (202609161000) — server-sorted; -1 keeps unscored deals below
  // a 1 on the export path.
  hotScore:     (d) => (d.hotScore ?? -1),
  lastActivityAt: (d) => d.lastActivityAt || '',
  feesInvoiced: (d) => d.totalFeesInvoiced,
  feesReceived: (d) => d.totalFeesReceived,
};

// "Group by" (table layout): partition the filtered rows under collapsible
// section headers. Everything grouped on is already on the row (stage slug,
// dealmaker name, industry name), so grouping is purely client-side and
// preserves the active column sort inside each group (the partition is
// stable). Stage groups follow pipeline order; the others sort
// alphabetically with the blank bucket last. Groups only span the rows
// loaded so far — same caveat as the Board, and the "shown · in this tab"
// meta already names the gap.
const GROUP_SPECS = {
  stage: {
    label: 'Stage',
    key:   (d) => d.stage || '',
    name:  (d, stageBySlug) => stageBySlug[d.stage]?.name || d.stageName || d.stage || 'No stage',
    order: (g, stageBySlug) => stageRank(g.key, stageBySlug),
    hue:   (g) => STAGE_HUE[g.key] || 'slate',
  },
  owner: {
    label: 'Dealmaker',
    key:   (d) => (d.owner && d.owner !== '—' ? d.owner : ''),
    name:  (d) => (d.owner && d.owner !== '—' ? d.owner : 'No dealmaker'),
  },
  sector: {
    label: 'Industry',
    key:   (d) => (d.sector && d.sector !== '—' ? d.sector : ''),
    name:  (d) => (d.sector && d.sector !== '—' ? d.sector : 'No industry'),
  },
};

const buildGroups = (rows, groupBy, stageBySlug) => {
  const spec = GROUP_SPECS[groupBy];
  if (!spec) return null;
  const byKey = new Map();
  rows.forEach((d) => {
    const key = spec.key(d);
    let g = byKey.get(key);
    if (!g) { g = { key, name: spec.name(d, stageBySlug), items: [] }; byKey.set(key, g); }
    g.items.push(d);
  });
  const groups = [...byKey.values()];
  groups.forEach((g) => {
    g.hue = spec.hue ? spec.hue(g) : null;
    g.fees = g.items.reduce((s, d) => s + (d.size || 0), 0);
  });
  groups.sort(spec.order
    ? (a, b) => spec.order(a, stageBySlug) - spec.order(b, stageBySlug)
    : (a, b) => ((a.key === '') - (b.key === '')) || a.name.localeCompare(b.name));
  return groups;
};

// Identifier columns appended to every engagement export regardless of the
// user's layout, so a file can always be joined back to Atlas. Everything
// else comes from the visible columns — see window.exportColumnsFor.
const EXPORT_ALWAYS = [
  { key: 'id', label: 'Atlas id', get: (d) => d.id },
];

// Deep-link senders (sidebar Mine bucket, Home Mine card, self-scoped
// pipeline rows) still say 'mine' — that's Active + Mine scope now that
// ownership is a switch, not a tab.
const mapDeepTab = (t) => (t === 'mine' ? 'active' : t);
// Mine vs Team is a sticky personal preference with a role-based first-run
// default. Shared via window.* (cross-file rule) so the sidebar's engagement
// buckets can show the numbers this page will actually open.
const SCOPE_KEY = 'atlas:engagements-scope';
// Sticky like the column widths — a grouping choice is how someone reads
// the grid, not a per-visit filter.
const GROUPBY_KEY = 'atlas:engagements-groupby';
window.AtlasEngScope = {
  // First-run default by role: dealmakers and senior associates own a book
  // → Mine; associates support the whole team's book and saw "almost
  // nothing" under Mine → Team. Admins and marketing get the All-teams
  // header seeded (api.jsx seedAllTeamsDefault), which forces Team here
  // anyway — the 'team' return covers them if they flip back to My team.
  // Unknown/unloaded role falls back to Mine (the pre-existing default,
  // and the effect below corrects it once the role arrives).
  defaultFor: (role) => {
    const r = (role || '').toLowerCase();
    return (r === 'associate' || r === 'marketing' || r === 'admin') ? 'team' : 'mine';
  },
  hasStored: () => {
    try { const v = window.localStorage.getItem(SCOPE_KEY); return v === 'mine' || v === 'team'; } catch { return false; }
  },
  get: (role) => {
    // The header's All-teams toggle is an explicit "show me everything" —
    // reloading into a view still pinned to yourself made the toggle look
    // broken ("changed to all teams, looked like loading, then came back
    // to only Cory"). Stored/default preferences apply in My-team mode
    // only; the in-page switch still works either way.
    if (window.AtlasApi?.isAllTeams?.()) return 'team';
    try {
      const v = window.localStorage.getItem(SCOPE_KEY);
      if (v === 'mine' || v === 'team') return v;
    } catch {}
    return window.AtlasEngScope.defaultFor(role);
  },
  set: (s) => {
    try { window.localStorage.setItem(SCOPE_KEY, s); } catch {}
    window.dispatchEvent(new CustomEvent('atlas-eng-scope', { detail: s }));
  },
};

// An any-of filter's picks as a clean array. A bare string still counts as
// one pick, so a caller that sets the old single-value shape keeps working.
const anyOf = (v) => (Array.isArray(v) ? v : (v ? [v] : []))
  .map((x) => String(x).trim()).filter(Boolean);

// Server-side list params for one engagements tab — filter-only (no paging
// keys). Pure, so the desktop grid and the phone list (mobile/deals.jsx)
// select exactly the same rows for the same tab/scope/filters and their
// counts can't drift apart. `filters` may be partial (the phone only sets a
// few); missing keys mean "any".
const buildEngagementParams = ({ tab, scope, userId, q, filters = {}, sortKey, sortDir }) => {
  // Each tab's params must select exactly the set its own pill counts —
  // those counts come from the server's engagement_breakdown (Team scope)
  // or engagement_breakdown_mine (Mine scope), where every bucket counts
  // DEALS: no buyer shells (per-pursuit rows under a seller deal; the
  // Buyers pill browses those, firm-grouped). Active additionally means
  // *live* — not exited, not parked (exclude_on_hold; active_only alone
  // only drops exited rows) and not terminal-stage. Won is the positive
  // of that exclusion: terminal stage but no exit recorded yet — done
  // deals waiting for someone to record the exit, after which they move
  // to Exited. All spans every status, archived included, so it reads as
  // roughly the sum of the other tabs (it was the raw engagements total
  // once, and ~1K invisible shells made "All 2,129" over a 1,100-row
  // strip look like a miscount).
  const stages = filters.stages || [];
  const p = { my_team: true, exclude_engagement_type: 'buyer_buyside' };
  // Ownership is the Mine/Team scope, applied to every tab. An explicit
  // dealmaker pick below still overrides it (viewing a colleague's book).
  if (scope === 'mine' && userId) p.dealmaker_id = userId;
  // An explicit stage pick beats the terminal-stage hiding (like the
  // prospect rule in setFiltersSmart): selecting the Won chip should show
  // the won rows, not an empty grid.
  if (tab === 'active')   { p.active_only  = true; p.include_archived = false; p.exclude_on_hold = true; p.exclude_terminal_stage = !stages.length; }
  if (tab === 'held')     { p.on_hold_only = true; p.include_archived = false; }
  if (tab === 'won')      { p.active_only  = true; p.include_archived = false; p.terminal_stage_only = true; }
  if (tab === 'exited')   { p.exited_only  = true; p.include_archived = false; }
  if (tab === 'archived') { p.archived_only = true; }
  // tab='all' → no status filter
  if (q) p.q = q;
  // Location filters are server-side (they join the company table). Each is
  // any-of: an array goes out as repeated params (?hq_state=a&hq_state=b).
  if (anyOf(filters.hqState).length) p.hq_state = anyOf(filters.hqState);
  if (anyOf(filters.hqCity).length)  p.hq_city  = anyOf(filters.hqCity);
  // Hot score as a min/max pair, so the server needs no operator vocabulary:
  // exactly N → [N, N]; below N → [–, N-1]; N and above → [N, –].
  if (filters.hotScore && filters.hotScore.n != null) {
    const { n, op } = filters.hotScore;
    if (op === 'lt')       p.max_hot_score = n - 1;
    else if (op === 'gte') p.min_hot_score = n;
    else { p.min_hot_score = n; p.max_hot_score = n; }
  }
  // Dealmaker / industry / single-stage filters are server-side too, so
  // they match the whole engagement set — filtering only the pages already
  // scrolled into `items` silently hid every match that hadn't loaded yet.
  // An explicit dealmaker pick overrides the Mine tab's own dealmaker_id.
  // The API takes one stage_slug; a multi-stage selection falls back to
  // the client-side pass below (rows are re-checked there either way).
  if (filters.dealmaker) p.dealmaker_id = filters.dealmaker;
  // CSV, like the hold chips: the API overlaps the uuid[] against the picks.
  // Server-side rather than client-only because the answer has to span the
  // whole tab, not the pages scrolled so far.
  if ((filters.dealTeam || []).length) p.deal_team_ids = filters.dealTeam.join(',');
  if (anyOf(filters.industry).length)   p.industry_id    = anyOf(filters.industry);
  if (anyOf(filters.leadSource).length) p.lead_source_id = anyOf(filters.leadSource);
  // Not on Won: it already pins the stage via terminal_stage_only, and a
  // leftover chip from another tab would silently override that.
  if (stages.length === 1 && tab !== 'won') p.stage_slug = stages[0];
  // Hold sub-filters only mean something where on-hold rows live — the
  // popover only offers them on the held tab, and a leftover pick applied
  // on Active (which excludes on-hold rows outright) would blank the grid.
  // CSV: the API filters with IN over the exact values.
  if (tab === 'held') {
    if ((filters.holdPriority || []).length) p.on_hold = filters.holdPriority.join(',');
    if ((filters.holdReason || []).length)   p.on_hold_reason = filters.holdReason.join(',');
  }
  // Column sorts are resolved server-side (see _SORTABLE in
  // routes/engagements.py). Sorting in the client only ever ordered the
  // pages already fetched, so on a 3.8K-row tab it ranked a 120-row window
  // and presented that as the whole truth — "Est. fees ↓" topped out at
  // $1.0M on a pipeline whose largest active fee is $4.0M.
  if (SERVER_SORT_KEYS.has(sortKey)) { p.sort = sortKey; p.sort_dir = sortDir; }
  return p;
};

// The Filter popover's starting point. Module-level so the URL (list_state.jsx)
// can leave every untouched key out of the link and read it back typed.
const ENG_FILTER_DEFAULTS = {
  stages:        [],        // empty = all stages
  dealmaker:     '',        // user_id filter (empty = any)
  // Deal team (202609151200) — team_member ids, OR'd (empty = any). Multi
  // because "deals Samuel or Michael is on" is the question; the Dealmaker
  // filter above stays single because a deal has one owner.
  dealTeam:      [],
  // Any-of picks from here down to hqCity (empty = any): a deal matches
  // if it matches ONE of the picked values.
  industry:      [],        // industry_ids
  leadSource:    [],        // lead_source_ids (142)
  // Location (on the linked company). Applied server-side via buildParams
  // so it searches the whole engagement set, not just loaded rows.
  hqState:       [],        // company HQ states, case-insensitive exact
  hqCity:        [],        // company HQ cities, substring
  minFees:       '',        // string for the input; coerced to number when applied
  // Hot score (202609161000): '' = any, else { n, op } — op 'eq' (exactly
  // n), 'lt' (below n) or 'gte' (n and above). Unscored deals never match.
  hotScore:      '',
  // On-hold sub-filters (held tab only). The hold pill carries two facts —
  // a priority (on_hold: high/low/repricing) and a delay type
  // (on_hold_reason: owner/business, migration 071) — and both are
  // filterable. Server-side via buildParams (CSV) so a pick matches the
  // whole tab, not just the pages scrolled in so far.
  holdPriority:  [],        // on_hold values (empty = any)
  holdReason:    [],        // on_hold_reason values (empty = any)
  // After the all-teams migration the Inbox suddenly held ~3.9K active
  // engagements. Most of the volume is at `prospect` — early-stage rows
  // that don't need daily attention. Default-hide them; the toggle in the
  // Filter popover brings them back when wanted.
  hideProspect:  true,
  // Opt-in "show only deals that moved recently" — useful when sorting
  // through the long tail to find what's actually live. We never auto-
  // archive based on this; it's purely a view filter.
  hideStale180:  false,
};
const ENG_TABS = new Set(['all', 'active', 'held', 'won', 'exited', 'archived']);

const DealsView = ({ stages, activeUser, counts, setDealId, setView, setCompanyId, initialStageFilter, initialDealmaker, initialTab, initialSort, tabDeepLink }) => {
  // iPad keeps this grid; only the chrome around it (tab strip) adapts so it
  // scrolls instead of clipping at portrait widths. Desktop is untouched.
  const device = window.useDevice();
  // Back from a record lands on the URL this list last wrote (#/deals?…, see
  // list_state.jsx): tab, scope, sort, search and filters come back from it.
  // A deep link (a Home card, a stage row, a dealmaker drill) names its own
  // view and wins. Read once, at mount — later changes only write.
  const deepLinked = Boolean(initialTab || initialStageFilter || initialDealmaker);
  const [urlState] = React.useState(() => {
    if (deepLinked) return null;
    const q = window.AtlasListState.queryOf('deals');
    return q ? window.AtlasListState.decode(q, { filters: ENG_FILTER_DEFAULTS }) : null;
  });
  // Tabs are pure lifecycle status; ownership is the orthogonal Mine/Team
  // scope below. The old Mine tab folded the two axes into one strip, which
  // left "my on-hold deals" with no home (712 parked rows reachable only
  // through a banner link).
  // Deep-linked from the dashboard's "Pipeline by stage": land on the Active
  // tab (that panel is computed from active deals) showing just that stage.
  // Deep-linked from a clickable dealmaker in Analytics: land on All, filtered.
  // Deep-linked from a Home metric card: land on the tab it counts.
  const [tab, setTab] = React.useState(
    initialTab ? mapDeepTab(initialTab)
      : (initialStageFilter ? 'active' : (initialDealmaker ? 'all'
        : (ENG_TABS.has(urlState?.tab) ? urlState.tab : 'active')))
  );    // all | active | held | exited | archived
  // Mine pins every tab to the signed-in user's own book, so "my on-hold
  // deals" is just the On hold tab. A deep-linked number decides its own
  // scope — the list must match the number that was clicked, and every
  // count except 'mine' is team-wide.
  const [scope, setScope] = React.useState(() => {
    if (initialTab) return initialTab === 'mine' ? 'mine' : 'team';
    if (initialStageFilter || initialDealmaker) return 'team';
    if (urlState?.scope === 'mine' || urlState?.scope === 'team') return urlState.scope;
    return window.AtlasEngScope.get(activeUser?.role);
  });
  // The URL names a scope only when it differs from what AtlasEngScope.get
  // would pick anyway (stored choice, role default, the All-teams rule), so a
  // reload never freezes a role default into the link. The header's
  // All-teams toggle drops it before reloading (app.jsx ScopeToggle).
  const changeScope = React.useCallback((s) => {
    setScope(s);
    window.AtlasEngScope.set(s);
  }, []);
  // The role can arrive after mount (activeUser loads async on a cold boot).
  // Only the role-derived DEFAULT may move the scope — a stored choice or a
  // deep link always wins.
  React.useEffect(() => {
    if (deepLinked || urlState?.scope) return;
    if (!activeUser?.role || window.AtlasEngScope.hasStored()) return;
    setScope(window.AtlasEngScope.get(activeUser.role));
  }, [activeUser?.role]);
  const [layout, setLayout] = React.useState(urlState?.layout === 'kanban' ? 'kanban' : 'table');
  // Deals grid vs Buyers directory. Buyer shells are per-pursuit rows, not
  // deals someone works — every tab excludes them server-side, and the
  // Buyers pill switches to the firm-grouped BuyerFirmsView instead.
  const [type, setType] = React.useState(urlState?.type === 'buyer_buyside' ? 'buyer_buyside' : 'seller_buyside');
  const [formulaDefs, setFormulaDefs] = React.useState([]);
  const [items, setItems] = React.useState(null);  // null = loading first page
  const [total, setTotal] = React.useState(0);
  // Inline cell editing (table layout): reuse EditableCell + updateEngagement,
  // optimistic row swap; EditableCell reverts its draft if the PATCH throws.
  const [members, setMembers] = React.useState([]);
  React.useEffect(() => { window.AtlasData.loadTeamMembers().then((m) => setMembers(Array.isArray(m) ? m : [])).catch(() => {}); }, []);
  const saveEngagement = React.useCallback(async (id, patch) => {
    const updated = await window.AtlasApi.updateEngagement(id, patch);
    setItems((prev) => (prev || []).map((x) => (x.id === id ? window.AtlasData.__adaptEngagement(updated) : x)));
    return updated;
  }, []);
  // Stage / hold / exit moves offer Undo (views/stage_moves.jsx). A move that
  // takes the row out of this tab leaves it in place, greyed, for the Undo
  // window, then drops it (the Engagements-list version of the task linger).
  const [fading, setFading] = React.useState(() => new Set());
  const fadeTimers = React.useRef({});
  React.useEffect(() => () => Object.values(fadeTimers.current).forEach(clearTimeout), []);
  const unfade = React.useCallback((id) => {
    clearTimeout(fadeTimers.current[id]);
    delete fadeTimers.current[id];
    setFading((prev) => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; });
  }, []);
  const fadeOut = React.useCallback((id) => {
    setFading((prev) => new Set(prev).add(id));
    clearTimeout(fadeTimers.current[id]);
    fadeTimers.current[id] = setTimeout(() => {
      unfade(id);
      setItems((prev) => (prev || []).filter((x) => x.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    }, window.StageUndo.LINGER_MS + 300);
  }, [unfade]);
  const saveMove = React.useCallback(async (d, patch, leavesTab) => {
    const L = window.StageMovesLogic;
    const before = L.stageSnapshot(d._raw);
    const updated = await saveEngagement(d.id, patch);
    const offered = window.StageUndo.offer({
      id: d.id, name: d.name, before, after: L.stageSnapshot(updated),
      undo: async (back) => {
        unfade(d.id);
        setHoldPrompt((p) => (p && p.id === d.id ? null : p));
        await saveEngagement(d.id, back);
        if (leavesTab) window.dispatchEvent(new CustomEvent('atlas-data-changed'));
      },
    });
    if (leavesTab) {
      // Nothing to undo (a Company sold exit): the row leaves at once.
      if (offered) fadeOut(d.id);
      else { setItems((prev) => (prev || []).filter((x) => x.id !== d.id)); setTotal((t) => Math.max(0, t - 1)); }
      window.dispatchEvent(new CustomEvent('atlas-data-changed'));
    }
    return updated;
  }, [saveEngagement, fadeOut, unfade]);
  const saveStage = React.useCallback((d, stage_id) => saveMove(d, { stage_id }, false), [saveMove]);
  // Hold picks from the stage dropdown. Same contract as the deal page's
  // On-hold select: PATCH on_hold alone (the stage stays put), then ask which
  // delay type it is when the deal was just parked without one.
  const [holdPrompt, setHoldPrompt] = React.useState(null);   // { id, name } | null
  const saveHold = React.useCallback(async (d, on_hold) => {
    // Active excludes parked rows and On hold lists only them (buildParams),
    // so a hold flip moves the deal between tabs server-side. Mirror that
    // here: the optimistic row swap alone left a just-parked deal sitting on
    // Active until the next reload. The tab pills re-pull on the event.
    await saveMove(d, { on_hold }, (tab === 'active' && on_hold) || (tab === 'held' && !on_hold));
    if (on_hold && (!d.onHold || !d.onHoldReason)) setHoldPrompt({ id: d.id, name: d.name, reason: d.onHoldReason, cadence: d.holdCheckinCadence });
  }, [saveMove, tab]);
  // "Next action" inline add: POST a 'Next Action' note — the server replaces
  // the engagement's next_action_notes/next_action_date (what DealCloud shows
  // as "Next Action") and keeps the previous value as note history, same as
  // the deal-page composer. Then refetch the row so next_action_* and
  // latest_note_* land exactly as the server serializes them.
  const saveNextAction = React.useCallback(async (d, { body, date }) => {
    await window.AtlasApi.createNote({
      body,
      note_type: 'general',
      dc_note_subtype: 'Next Action',
      next_action_date: date || null,
      engagement_id: d.id,
      company_id: d.target || null,
      author_id: activeUser?.id || null,
      author_name: activeUser?.full_name || null,
    });
    const raw = await window.AtlasApi.getEngagement(d.id);
    setItems((prev) => (prev || []).map((x) => (x.id === d.id ? window.AtlasData.__adaptEngagement(raw) : x)));
  }, [activeUser?.id, activeUser?.full_name]);
  // Cursor-style pagination: start at page 1, append on scroll-to-end.
  // Use ONE limit for every page so the server's offset math
  // ( (page-1)*limit ) lines up with our cumulative `items.length` —
  // mixing 60/120 here skipped rows 60-120 and caused runaway pagination
  // because items.length could never catch up to total.
  const [page, setPage] = React.useState(1);
  const [paging, setPaging] = React.useState(false);
  // `loadedAll` is a hard stop for the IntersectionObserver: flipped true
  // either when items.length >= total OR when a fetch returned 0 rows.
  // Defensive against any future server-side filter mismatch where total
  // might overstate what we can actually retrieve.
  const [loadedAll, setLoadedAll] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 120;
  // Where this list sits in the URL (#/deals?…), and the scroll offset saved
  // for it when it was last left. A saved offset past the first page loads
  // that many pages up front (reload → fetchPage `pages`), capped at 20.
  const listUrlRef = React.useRef(window.AtlasListState.queryOf('deals') != null ? window.location.hash : null);
  const scrollRef = React.useRef(null);
  const scrollTopRef = React.useRef(0);
  const loadedCountRef = React.useRef(0);
  const restoreRef = React.useRef(null);   // null = not read yet, false = nothing to restore
  // Only the deals table keeps an offset (see the restore effect below).
  const tableShownRef = React.useRef(false);
  if (restoreRef.current === null) {
    const tableUrl = urlState?.layout !== 'kanban' && urlState?.type !== 'buyer_buyside';
    const saved = !deepLinked && tableUrl && listUrlRef.current ? window.AtlasListState.takeScroll(listUrlRef.current) : null;
    restoreRef.current = saved
      ? { top: saved.top, pages: Math.min(20, Math.max(1, Math.ceil((saved.count || 0) / PAGE_SIZE))) }
      : false;
  }
  // Sort + filter live here (above the table) so they survive table re-renders
  // and so we can both display indicators in the header chrome and apply them
  // before passing rows down. Default sort comes from the tab
  // (TAB_DEFAULT_SORT): Active orders by pipeline position, the rest by
  // most-recently-updated. A card that drills into a *sum* (Pipeline est.
  // fees) overrides it — landing on the default order shows rows in an order
  // that has nothing to do with the number that was clicked.
  const startSort = initialSort?.key ? initialSort : (urlState?.sort || null);
  const [sortKey, setSortKey] = React.useState(startSort?.key || TAB_DEFAULT_SORT(tab).key);
  const [sortDir, setSortDir] = React.useState(startSort?.dir || TAB_DEFAULT_SORT(tab).dir);  // asc | desc
  // True once the user has picked a sort themselves (header click, or a
  // deep link that names one). Tab switches stop re-applying the tab
  // default from then on — a chosen sort is a choice, not view state.
  const userSortedRef = React.useRef(Boolean(startSort?.key));
  // State twin of the ref, for the URL write: a picked sort is written even
  // when it equals the tab default, so Back restores it as a choice.
  const [userSorted, setUserSorted] = React.useState(userSortedRef.current);
  const markSorted = React.useCallback((v) => { userSortedRef.current = v; setUserSorted(v); }, []);
  const changeTab = React.useCallback((t) => {
    setTab(t);
    if (!userSortedRef.current) {
      const d = TAB_DEFAULT_SORT(t);
      setSortKey(d.key); setSortDir(d.dir);
    }
  }, []);
  const [filterOpen, setFilterOpen] = React.useState(false);
  // Group-by (table layout only — the Board is already grouped by stage).
  const [groupBy, setGroupBy] = React.useState(() => {
    try { const v = window.localStorage.getItem(GROUPBY_KEY); return GROUP_SPECS[v] ? v : ''; } catch { return ''; }
  });
  const changeGroupBy = React.useCallback((g) => {
    setGroupBy(g);
    try { window.localStorage.setItem(GROUPBY_KEY, g); } catch {}
  }, []);
  const [creating, setCreating] = React.useState(false);
  // Defaults live in ENG_FILTER_DEFAULTS. A deep link to a stage or a Home
  // metric card shows prospect rows: its number counts every stage, so hiding
  // them would show a list that visibly contradicts the number just clicked.
  const [filters, setFilters] = React.useState(() => ({
    ...ENG_FILTER_DEFAULTS,
    stages:        initialStageFilter ? [initialStageFilter] : [],
    dealmaker:     initialDealmaker?.id || '',
    hideProspect:  (initialStageFilter || initialTab) ? false : true,
    ...(urlState?.filters || null),
  }));
  // Free-text search box. Debounced into `searchQ`, which feeds the server-side
  // `q` param (matches engagement name + notes) so it finds rows beyond the
  // ones already scrolled into `items` — the key to telling apart same-named
  // buyer engagements that differ only by company.
  const [search, setSearch] = React.useState(urlState?.q || '');
  const [searchQ, setSearchQ] = React.useState(urlState?.q || '');
  React.useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    window.AtlasApi.listFieldDefinitions('engagement')
      .then((defs) => setFormulaDefs((defs || []).filter((d) => d.field_type === 'formula' && d.active)))
      .catch(() => {});
  }, []);

  const stageList = (stages && stages.length) ? stages : null;
  const stageBySlug = React.useMemo(() => {
    const m = {};
    (stageList || []).forEach((s) => { m[s.slug] = s; });
    return m;
  }, [stageList]);
  // Exit picks from the stage dropdown open the deal page's exit drawer for
  // that row; the save is the PATCH the deal page makes. Active and Won list
  // only live deals (exit_id IS NULL in buildParams), so the row leaves those
  // tabs immediately, the way a hold pick does — On hold keeps exited holds
  // server-side, so there the row stays and just re-renders as Exited.
  const [exitTarget, setExitTarget] = React.useState(null);   // grid row | null
  const openExit = React.useCallback((d) => setExitTarget(d), []);
  // A stage pick of Won opens the Closing drawer (202609301300); the PATCH
  // carries the closing so the server records it before the stage flips.
  const [wonTarget, setWonTarget] = React.useState(null);   // { d, stageId } | null
  const openWon = React.useCallback((d, stageId) => setWonTarget({ d, stageId }), []);
  const saveWon = React.useCallback(async (closing) => {
    const { d, stageId } = wonTarget;
    await saveMove(d, { stage_id: stageId, closing }, tab === 'active');
    setWonTarget(null);
  }, [wonTarget, saveMove, tab]);
  const exitsFor = React.useCallback((d) => (
    d ? (stageBySlug[d.stage]?.exits || []).filter((x) => x.active !== false) : []
  ), [stageBySlug]);
  const saveExit = React.useCallback(async (exit_id, exit_notes) => {
    const d = exitTarget;
    if (!d) return;
    await saveMove(d, { exit_id, exit_notes: exit_notes || null }, tab === 'active' || tab === 'won');
    setExitTarget(null);
  }, [exitTarget, saveMove, tab]);

  // Build server-side params from the current tab.
  // Filter-only params (no pagination keys). buildParams() is the cache
  // key for changes that should reset the list (tab change, user change),
  // distinct from page changes that should append.
  // The rules live in buildEngagementParams (shared with the phone list).
  const buildParams = React.useCallback(() => buildEngagementParams({
    tab, scope, userId: activeUser?.id, q: searchQ, filters, sortKey, sortDir,
  }), [tab, scope, activeUser?.id, searchQ, filters.hqState, filters.hqCity, filters.hotScore,
      filters.dealmaker, filters.dealTeam, filters.industry, filters.leadSource, filters.holdPriority,
      filters.holdReason, filters.stages, sortKey, sortDir]);

  // Fetch one page. `append=true` adds onto `items`; otherwise replaces.
  // Loading flag prevents concurrent loadMore() calls when the user scrolls
  // fast or the IntersectionObserver fires repeatedly.
  // Stale-response guard — a slow response for an older query/filter combo
  // must not overwrite fresher rows (debounced-search race).
  const reqSeq = React.useRef(0);

  // `pages` > 1 (first page only) loads that many pages in one call — how a
  // restored scroll offset gets the rows it lands on (see restoreRef).
  const fetchPage = React.useCallback(async (p, append, pages = 1) => {
    const seq = ++reqSeq.current;
    setPaging(true);
    try {
      const res = await window.AtlasApi.listEngagements({
        ...buildParams(), page: p, limit: PAGE_SIZE * pages,
      });
      if (seq !== reqSeq.current) return;   // superseded — drop silently
      const rows = ((res && res.items) || []).map(window.AtlasData.__adaptEngagement);
      const newTotal = res?.total ?? rows.length;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        // Hard stop: an empty page means we hit the end, regardless of
        // what `total` says. Also flip when we've definitively caught up.
        if (rows.length === 0 || next.length >= newTotal) {
          setLoadedAll(true);
        }
        return next;
      });
      setTotal(newTotal);
      setPage(p + pages - 1);
    } catch {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setTotal(0); setLoadedAll(true); }
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [buildParams]);

  // An explicit stage or dealmaker pick must beat the default prospect-
  // hiding: selecting the Prospect chip used to return zero rows (the
  // hideProspect pass ran after the stage filter), and an associate's book
  // — almost all prospect-stage — looked empty when filtered to them.
  const setFiltersSmart = React.useCallback((next) => {
    setFilters((prev) => {
      const f = typeof next === 'function' ? next(prev) : next;
      const pickedProspect  = (f.stages || []).includes('prospect') && !(prev.stages || []).includes('prospect');
      const pickedDealmaker = f.dealmaker && f.dealmaker !== prev.dealmaker;
      return (pickedProspect || pickedDealmaker) && f.hideProspect
        ? { ...f, hideProspect: false }
        : f;
    });
  }, []);

  // Row selection (the checkbox column) — feeds Export. Cleared on every
  // list reset: ids carried across a tab/scope/filter change would silently
  // widen the next export beyond what's on screen.
  const [selected, setSelected] = React.useState(() => new Set());
  const [importing, setImporting] = React.useState(false);  // bulk update from a file
  const [bulkEditing, setBulkEditing] = React.useState(false);  // set a field on the checked rows
  const toggleRow = React.useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Stable identities — these feed DealsTable's memoized cellCtx, so an
  // inline arrow here would re-render every row on every parent render.
  const openDeal = React.useCallback((id) => { setView('deal'); setDealId(id); }, [setView, setDealId]);
  const openCompany = React.useMemo(
    () => (setCompanyId ? (id) => { setView('company'); setCompanyId(id); } : null),
    [setView, setCompanyId]);

  const reload = React.useCallback(() => {
    setItems(null);
    setPage(1);
    setLoadedAll(false);
    setSelected(new Set());
    return fetchPage(1, false, restoreRef.current?.pages || 1);
  }, [fetchPage]);

  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    const refresh = () => reload();
    window.addEventListener('deal-updated', refresh);
    return () => window.removeEventListener('deal-updated', refresh);
  }, [reload]);

  // Mirror the view into the URL. One task later: on mount this effect runs
  // before app.jsx's writeHash (child effects first), and writing then would
  // replace the previous page's history entry instead of the list's own.
  React.useEffect(() => {
    const t = setTimeout(() => {
      const url = window.AtlasListState.write('deals', window.AtlasListState.encode(
        { tab, scope, sort: { key: sortKey, dir: sortDir }, q: searchQ, layout, type, filters },
        { tab: 'active', scope: window.AtlasEngScope.get(activeUser?.role),
          sort: userSorted ? null : TAB_DEFAULT_SORT(tab),
          layout: 'table', type: 'seller_buyside', filters: ENG_FILTER_DEFAULTS },
      ));
      if (url) listUrlRef.current = url;
    }, 0);
    return () => clearTimeout(t);
  }, [tab, scope, sortKey, sortDir, userSorted, searchQ, layout, type, filters, activeUser?.role]);

  // A different #/deals?… link opened while the list is already up (pasted,
  // or another page's link): the useState initializers above never re-run,
  // so apply it here. The bare #/deals is left alone — app.jsx writes it
  // when a deep link lands, and the deep link has already set the view.
  // Our own writes are replaceState, which fires no hashchange.
  React.useEffect(() => {
    const onHash = () => {
      const q = window.AtlasListState.queryOf('deals');
      if (!q || window.location.hash === listUrlRef.current) return;
      const st = window.AtlasListState.decode(q, { filters: ENG_FILTER_DEFAULTS });
      const nextTab = ENG_TABS.has(st.tab) ? st.tab : 'active';
      restoreRef.current = false;
      listUrlRef.current = window.location.hash;
      setTab(nextTab);
      setScope(st.scope === 'mine' || st.scope === 'team' ? st.scope : window.AtlasEngScope.get(activeUser?.role));
      const srt = st.sort || TAB_DEFAULT_SORT(nextTab);
      markSorted(Boolean(st.sort));
      setSortKey(srt.key); setSortDir(srt.dir);
      setSearch(st.q || ''); setSearchQ(st.q || '');
      setLayout(st.layout === 'kanban' ? 'kanban' : 'table');
      setType(st.type === 'buyer_buyside' ? 'buyer_buyside' : 'seller_buyside');
      setFilters({ ...ENG_FILTER_DEFAULTS, ...(st.filters || null) });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [activeUser?.role, markSorted]);

  // Scroll offset (refs declared by PAGE_SIZE above): tracked as it moves,
  // saved when the list unmounts (a row opened, the sidebar, Back), restored
  // once the rows that offset lands on are loaded.
  React.useEffect(() => { loadedCountRef.current = items ? items.length : 0; }, [items]);
  tableShownRef.current = layout === 'table' && type !== 'buyer_buyside';
  // Only the deals table restores: the Board and the Buyers directory render
  // other content in the same container, where the offset would clamp to 0.
  // A saved offset is consumed once the table actually reaches it (the
  // rows are in the DOM by the time this runs), or when there's nothing more
  // coming — not on the first render that merely isn't tall enough yet.
  const restoreAt = React.useRef({ layout, type });
  React.useEffect(() => {
    const r = restoreRef.current;
    if (!r) return;
    if (layout !== restoreAt.current.layout || type !== restoreAt.current.type) { restoreRef.current = false; return; }
    if (layout !== 'table' || type === 'buyer_buyside' || items === null || !scrollRef.current) return;
    const el = scrollRef.current;
    el.scrollTop = r.top;
    scrollTopRef.current = el.scrollTop;
    if (el.scrollTop >= r.top - 1 || loadedAll || items.length >= r.pages * PAGE_SIZE) restoreRef.current = false;
  }, [items, layout, type, loadedAll]);
  React.useEffect(() => () => {
    if (tableShownRef.current) window.AtlasListState.saveScroll(listUrlRef.current, scrollTopRef.current, loadedCountRef.current);
  }, []);

  // Deep-link from a clickable dealmaker name in Analytics: filter to them and
  // reveal prospect-stage deals (hidden by default), so their full book shows.
  // Team scope — under Mine the tab pills would describe the viewer's own
  // book while the grid shows the colleague's.
  React.useEffect(() => {
    if (initialDealmaker && initialDealmaker.id) {
      changeTab('all');
      setScope('team');
      setFilters((f) => ({ ...f, dealmaker: initialDealmaker.id, hideProspect: false }));
    }
  }, [initialDealmaker]);

  // Tab deep-links (Home metric cards, sidebar buckets) can arrive while this
  // view is already mounted, where the useState initializers above never
  // re-run. Mirror them: land on the tab in the deals grid, counting every
  // stage (the clicked number does).
  // Keyed on the deep-link OBJECT — each click mints a fresh one, so
  // re-clicking a bucket re-lands its tab even if the tab string matches.
  React.useEffect(() => {
    if (!tabDeepLink?.tab) return;
    changeTab(mapDeepTab(tabDeepLink.tab));
    // A deep link can name its own scope (sidebar buckets follow the shared
    // scope, so their numbers open the matching list); legacy senders only
    // say 'mine'/team-wide-tab and fall back to the old mapping.
    setScope(tabDeepLink.scope || (tabDeepLink.tab === 'mine' ? 'mine' : 'team'));
    setType('seller_buyside');
    setFilters((f) => (f.hideProspect ? { ...f, hideProspect: false } : f));
    if (tabDeepLink.sort?.key) {
      markSorted(true);
      setSortKey(tabDeepLink.sort.key); setSortDir(tabDeepLink.sort.dir || 'desc');
    }
  }, [tabDeepLink, changeTab]);

  // Auto-load next page when the sentinel scrolls into view (within 240px
  // of the viewport). `hasMore` is the AND of two signals:
  //   • items.length < total       — server still has rows for us
  //   • !loadedAll                 — last fetch didn't come back empty
  // Both must be true. `loadedAll` is the safety net for any case where
  // the server's `total` overstates what's actually retrievable.
  const hasMore = items != null && !loadedAll && items.length < total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) {
        fetchPage(page + 1, true);
      }
    }, { rootMargin: '240px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  // Apply client-side filter then sort. We've already loaded up to 5,000 rows
  // server-side, so 2k-row in-memory sort is well under one frame.
  // A named callback (not inlined in the memo) because Export runs the same
  // pass over a full-tab fetch — the file must contain exactly what the grid
  // semantics would show, not the server superset.
  // The prospect flood guard below exists for a TEAM-wide view. When the
  // visible team collapses to one dealmaker — a one-person team, or a team
  // whose other members have left — Team scope is that person's own book,
  // and hiding its prospect rows produced "16 shown · 600 in this tab" over
  // a grid that would never show the other 584 (Thomas Klewer, Sell-Side
  // Team, 2026-09-10). Detect it from the counts endpoint: when the
  // team-scoped All bucket equals the signed-in user's All bucket, every
  // deal in scope is theirs. The endpoint already honours the header's
  // All-teams toggle (my_team=false widens the team bucket firm-wide), so
  // the guard stays on there. Unknown counts (still loading) keep the guard.
  const soloBook = React.useMemo(() => {
    const team = counts?.engagement_breakdown;
    const mine = counts?.engagement_breakdown_mine;
    if (!team || !mine || team.all == null || mine.all == null) return false;
    return team.all === mine.all;
  }, [counts]);
  const prospectGuardOff = scope === 'mine' || soloBook;

  // The column catalog. Admin-defined formula/AI fields join it, so they
  // hide and reorder like any built-in column instead of being pinned to the
  // far right. Defined here because the client filter pass below also reads
  // it — the Filter popover can filter on any of these columns.
  const engagementCatalog = React.useMemo(
    () => [...ENGAGEMENT_COLUMNS, ...window.formulaColumns(formulaDefs)],
    [formulaDefs]);

  const applyClientFilters = React.useCallback((list) => {
    // No type filter here: buyer shells are excluded server-side on every
    // tab, and rows with a NULL/other engagement_type are still deals — the
    // grid must show everything the tab pill counted.
    let r = list;
    if (filters.stages.length) {
      const s = new Set(filters.stages);
      r = r.filter((d) => s.has(d.stage));
    }
    if (filters.dealmaker) {
      r = r.filter((d) => d._raw?.dealmaker_id === filters.dealmaker);
    }
    if ((filters.dealTeam || []).length) {
      const s = new Set(filters.dealTeam);
      // "Any of the picked people is on this deal" — mirrors the server's &&.
      r = r.filter((d) => (d.dealTeam || []).some((id) => s.has(id)));
    }
    if (anyOf(filters.industry).length) {
      const s = new Set(anyOf(filters.industry));
      r = r.filter((d) => s.has(d._raw?.industry_id));
    }
    if (anyOf(filters.leadSource).length) {
      const s = new Set(anyOf(filters.leadSource));
      r = r.filter((d) => s.has(d._raw?.lead_source_id));
    }
    const minFeesNum = Number(filters.minFees);
    if (filters.minFees && !Number.isNaN(minFeesNum) && minFeesNum > 0) {
      r = r.filter((d) => (d.size ?? 0) >= minFeesNum);
    }
    if (filters.hotScore && filters.hotScore.n != null) {
      const { n, op } = filters.hotScore;
      r = r.filter((d) => d.hotScore != null
        && (op === 'lt' ? d.hotScore < n : op === 'gte' ? d.hotScore >= n : d.hotScore === n));
    }
    // Ad-hoc "any column" filters added from the Filter popover (col:*).
    r = window.applyColumnFilters(r, filters, engagementCatalog);
    // Same held-tab gate as buildParams — see the comment there.
    if (tab === 'held') {
      if ((filters.holdPriority || []).length) {
        const s = new Set(filters.holdPriority);
        r = r.filter((d) => s.has(d.onHold));
      }
      if ((filters.holdReason || []).length) {
        const s = new Set(filters.holdReason);
        r = r.filter((d) => s.has(d.onHoldReason));
      }
    }
    // Prospect-hiding is a flood guard for the team-wide view (~2.4K
    // prospect-stage sellers on prod). Mine scope is one person's book — a
    // dozen deals — and hiding its earliest-stage rows made deals silently
    // "missing" next to the owner's DealCloud view. Same for a one-person
    // team in Team scope (soloBook above).
    if (filters.hideProspect && !prospectGuardOff) {
      r = r.filter((d) => d.stage !== 'prospect' && d.stage !== 'ss_prospect');
    }
    if (filters.hideStale180) {
      const cutoff = Date.now() - 180 * 86400 * 1000;
      r = r.filter((d) => {
        const t = d._raw?.last_stage_update;
        if (!t) return true;  // keep rows with no stage history — can't judge
        return new Date(t).getTime() >= cutoff;
      });
    }
    // Server-sorted keys arrive in order — re-sorting here would only risk
    // disagreeing with SQL's collation and NULLS LAST on the page boundary.
    if (sortKey && !SERVER_SORT_KEYS.has(sortKey)) {
      r = [...r].sort(window.makeComparator(sortKey, sortDir, SORT_EXTRACT, stageBySlug));
    }
    return r;
  }, [filters, sortKey, sortDir, stageBySlug, prospectGuardOff, tab, engagementCatalog]);
  const rows = React.useMemo(() => applyClientFilters(items || []), [items, applyClientFilters]);

  // Per-user column layout (105). Lives here rather than in DealsTable so the
  // Columns button can sit in the toolbar next to Filter and Export.
  const columns = window.useColumnPrefs('engagements', engagementCatalog, {
    rows, fitMax: 360, legacyWidthKey: 'deals',
  });

  // Header checkbox: all visible rows selected → clear; anything else →
  // select all visible.
  const toggleAll = React.useCallback(() => {
    setSelected((prev) => (
      rows.length && rows.every((d) => prev.has(d.id))
        ? new Set()
        : new Set(rows.map((d) => d.id))
    ));
  }, [rows]);

  // Export the selection, or with nothing selected the whole current view.
  // The loaded pages may be a prefix of the tab, so the view-wide path
  // refetches everything in one call (server cap 10K; prod's biggest deal
  // bucket is ~2.4K) and runs the same client filter pass the grid uses.
  const doExport = React.useCallback(async (format) => {
    let data;
    if (selected.size) {
      data = rows.filter((d) => selected.has(d.id));
    } else if (loadedAll) {
      data = rows;
    } else {
      const res = await window.AtlasApi.listEngagements({ ...buildParams(), page: 1, limit: 10000 });
      data = applyClientFilters(((res && res.items) || []).map(window.AtlasData.__adaptEngagement));
    }
    // The file mirrors the grid: the columns you can see, in your order.
    const xcols = window.exportColumnsFor(columns.cols, EXPORT_ALWAYS);
    const records = window.exportRecords(xcols, data);
    const name = `engagements-${tab}${scope === 'mine' ? '-mine' : ''}-${window.localISODate()}`;
    if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
    else window.exportJson(`${name}.json`, records);
  }, [selected, rows, loadedAll, buildParams, applyClientFilters, tab, scope, columns.cols]);

  const toggleSort = React.useMemo(() => {
    const t = window.makeToggleSort(setSortKey, setSortDir);
    return (key) => { markSorted(true); t(key); };
  }, [setSortKey, setSortDir, markSorted]);

  // The Deals-pill count is computed from the LOADED rows, so it only
  // describes the whole tab once every page is in; `null` renders the pill
  // without a number rather than with a wrong one. The Buyers pill never
  // shows one here — shells aren't fetched into the grid on any tab, and
  // BuyerFirmsView reports its own firm count.
  const typeCounts = React.useMemo(() => {
    if (!items || !loadedAll) return { seller_buyside: null, buyer_buyside: null };
    return { seller_buyside: items.length, buyer_buyside: null };
  }, [items, loadedAll]);

  // Sub-bucket counts come from the dashboard counts endpoint — cheap SQL
  // aggregates, no bulk fetch. Updated by the App-level event listener.
  // The tab pills show the numbers for the CURRENT scope, so flipping
  // Mine/Team re-labels the whole strip; each pill still counts exactly
  // what its tab lists (the breakdown queries and buildParams apply the
  // same predicates).
  const breakdown = counts?.engagement_breakdown || {};
  const scopedCounts = scope === 'mine'
    ? (counts?.engagement_breakdown_mine || {})
    : breakdown;

  // `total` is the server's count for this tab + the server-side filters;
  // `rows` is what survives the client-only ones (stage multi-select,
  // hide-prospect, min-fees, hide-stale). Reporting `total` alone read as a
  // flat contradiction of the grid — "3,841 records" above 339 visible rows.
  // Name both numbers, and only when they actually differ.
  const countMeta = React.useMemo(() => {
    const shown = rows.length;
    if (!loadedAll || shown !== total) {
      return `${shown.toLocaleString()} shown · ${total.toLocaleString()} in this tab`;
    }
    return `${total.toLocaleString()} record${total === 1 ? '' : 's'}`;
  }, [rows.length, total, loadedAll]);

  // hideProspect isn't applied in Mine scope or for a one-person team, so it
  // mustn't light the Filter badge there — a "Filter 1" that filters nothing
  // is another mystery count.
  // Same for the hold sub-filters off the held tab: they're neither offered
  // nor applied there, so a selection carried over from Held mustn't count.
  const effectiveFilters = {
    ...filters,
    ...(prospectGuardOff ? { hideProspect: false } : null),
    ...(tab !== 'held' ? { holdPriority: [], holdReason: [] } : null),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Engagements']}
        title="Engagements"
        meta={items === null ? 'Loading…' : countMeta}
        actions={(
          <>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 8, display: 'inline-flex', color: 'var(--text-4)', pointerEvents: 'none' }}>
                <Icon.Search size={12} />
              </span>
              <TextInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search engagements…"
                style={{ width: 200, paddingLeft: 26 }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  title="Clear search"
                  style={{ position: 'absolute', right: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-4)', display: 'inline-flex', padding: 2 }}
                >
                  <Icon.Close size={11} />
                </button>
              )}
            </div>
            {/* Export the checked rows, or the whole current view when
                nothing is checked. Hidden on the Buyers directory — that
                view has its own data shape. */}
            {type !== 'buyer_buyside' && selected.size > 0 && (
              <Btn size="sm" variant="outline" icon={<Icon.Edit size={12} />} onClick={() => setBulkEditing(true)}
                title="Set one field on every checked deal">
                Edit {selected.size}
              </Btn>
            )}
            {type !== 'buyer_buyside' && (
              <Btn size="sm" variant="outline" icon={<Icon.Upload size={12} />} onClick={() => setImporting(true)}
                title="Update deals from a CSV or Excel file — preview before anything changes">Import</Btn>
            )}
            {type !== 'buyer_buyside' && (
              <window.ExportMenu
                disabled={items === null || (!rows.length && !selected.size)}
                selectedCount={selected.size}
                viewCount={loadedAll ? rows.length : total}
                onExport={doExport}
              />
            )}
            {layout === 'table' && (
              <window.ColumnsMenu
                catalog={engagementCatalog}
                cols={columns.cols}
                toggle={columns.toggle}
                move={columns.move}
                reset={columns.reset}
              />
            )}
            <div style={{ position: 'relative' }}>
              <Btn size="sm" variant={window.activeFilterCount(effectiveFilters) ? 'accent' : 'outline'}
                icon={<Icon.Filter size={12} />}
                onClick={() => setFilterOpen((v) => !v)}>
                Filter
                {window.activeFilterCount(effectiveFilters) > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10.5, opacity: 0.85 }}>
                    {window.activeFilterCount(effectiveFilters)}
                  </span>
                )}
              </Btn>
              {filterOpen && (
                <window.FilterPopover
                  filters={filters}
                  setFilters={setFiltersSmart}
                  onClose={() => setFilterOpen(false)}
                  columns={engagementCatalog}
                  spec={[
                    { key: 'stages',    label: 'Stage',           type: 'chips', col: 'stage',
                      options: (stageList || []).map((s) => ({ value: s.slug, label: s.name })) },
                    // Hold sub-filters — held tab only (elsewhere there are
                    // no on-hold rows to narrow, or the tab is a mixed bag
                    // where "on hold · high" would silently drop everything
                    // else). Values mirror the "On hold · high · owner
                    // delay" pill on each row.
                    ...(tab === 'held' ? [
                      { key: 'holdPriority', label: 'Hold priority', type: 'chips',
                        options: [
                          { value: 'high',      label: 'High' },
                          { value: 'low',       label: 'Low' },
                          { value: 'repricing', label: 'Repricing' },
                        ] },
                      { key: 'holdReason', label: 'Hold reason', type: 'chips', col: 'onHoldReason',
                        options: [
                          { value: 'owner_delay',    label: 'Owner delay' },
                          { value: 'business_delay', label: 'Business delay' },
                        ],
                        hint: 'What\'s holding the parked deal up. Deals parked before reasons existed have none set and won\'t match.' },
                    ] : []),
                    { key: 'dealmaker', label: 'Dealmaker',       type: 'select', col: 'owner',
                      optionsAsync: () => window.AtlasApi.listTeamMembers().then(
                        (m) => (Array.isArray(m) ? m : (m.items || []))
                          .filter((u) => u.active !== false)
                          .map((u) => ({ value: u.id, label: u.full_name || u.email }))
                      ) },
                    // Chips, not a select: several people at once is the
                    // point, and picks are OR'd the way the Stage chips are.
                    { key: 'dealTeam', label: 'Deal team',       type: 'chips', col: 'dealTeam',
                      optionsAsync: () => window.AtlasApi.listTeamMembers().then(
                        (m) => (Array.isArray(m) ? m : (m.items || []))
                          .filter((u) => u.active !== false)
                          .map((u) => ({ value: u.id, label: u.full_name || u.email }))
                      ),
                      hint: 'Deals any of the picked people is working. Separate from Dealmaker — the owner is not automatically on the deal team.' },
                    { key: 'industry',  label: 'Industry',        type: 'multi', col: 'sector',
                      placeholder: 'Type an industry…',
                      optionsAsync: () => window.AtlasApi.listIndustries().then(
                        (i) => (Array.isArray(i) ? i : (i.items || []))
                          .map((row) => ({ value: row.id, label: row.name }))
                      ) },
                    { key: 'leadSource', label: 'Lead source',    type: 'chips', col: 'leadSource',
                      optionsAsync: () => window.AtlasApi.listLeadSources().then(
                        (s) => (Array.isArray(s) ? s : (s.items || []))
                          .map((row) => ({ value: row.id, label: row.name }))
                      ),
                      hint: 'Where the lead first came from (inherited from the company). Deals with none recorded never match.' },
                    { key: 'hqState',   label: 'HQ state',        type: 'multi',
                      placeholder: 'e.g. Georgia or GA',
                      options: window.GEO.STATES.map((st) => ({ value: st, label: st, alias: window.GEO.STATE_CODES[st] })) },
                    { key: 'hqCity',    label: 'HQ city',         type: 'multi', free: true,
                      placeholder: 'e.g. Dallas', options: window.GEO.CITIES,
                      hint: 'Matches any city containing what you typed.' },
                    { key: 'minFees',   label: 'Min. est. fees ($)', type: 'number', col: 'fees',
                      placeholder: 'e.g. 250000' },
                    // Pick a score for exactly that score; "Less than" / "Greater
                    // than" widen it to below N / N and above.
                    { key: 'hotScore', label: 'Hot score',        type: 'score', col: 'hotScore',
                      hint: 'Your manual 1–10 priority for the deal. Unscored deals never match.' },
                    { key: 'hideProspect', label: 'Hide Prospect stage',
                      type: 'toggle',
                      hint: scope === 'mine'
                        ? 'Not applied in Mine scope — your own book always shows every stage.'
                        : soloBook
                          ? 'Not applied — every deal on your team is yours, so this view is your own book and shows every stage.'
                          : 'Default ON — most of the volume sits at prospect.' },
                    { key: 'hideStale180', label: 'Hide stale (>180d since last stage change)',
                      type: 'toggle',
                      hint: 'Useful when scanning for what\'s actually moving.' },
                  ]}
                />
              )}
            </div>
            {type !== 'buyer_buyside' && layout === 'table' && (
              <GroupMenu groupBy={groupBy} onChange={changeGroupBy} />
            )}
            {/* A client-only sort (notes, formula columns) can only order the
                pages fetched so far, so say so rather than let it read as a
                ranking of the whole tab. */}
            <Btn size="sm" variant="outline" icon={<Icon.Sort size={12} />}
              onClick={() => { markSorted(false); const d = TAB_DEFAULT_SORT(tab); setSortKey(d.key); setSortDir(d.dir); }}
              title={sortKey && !SERVER_SORT_KEYS.has(sortKey) && !loadedAll
                ? `This column is sorted across the ${items ? items.length.toLocaleString() : 0} rows loaded so far, not all ${total.toLocaleString()}. Scroll to load more, or sort by a different column. Click to reset to this tab's default.`
                : "Click a column header to sort. This resets to the tab's default (Active: stage · desc, others: updated · desc)."}>
              {sortKey ? `Sort: ${sortKey} ${sortDir === 'desc' ? '↓' : '↑'}${sortKey && !SERVER_SORT_KEYS.has(sortKey) && !loadedAll ? ' · loaded rows' : ''}` : 'Sort'}
            </Btn>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />}
              onClick={() => setCreating(true)}>New engagement</Btn>
          </>
        )}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: 1, padding: '0 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36,
        // Touch (iPad portrait): six tabs + three segmented controls are wider
        // than the screen — scroll the strip sideways rather than clip it.
        ...(device.touch ? { overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', flexShrink: 0 } : null),
      }}>
        {[
          ['all',      'All',       scopedCounts.all],
          ['active',   'Active',    scopedCounts.active],
          ['held',     'On hold',   scopedCounts.on_hold],
          ['won',      'Won',       scopedCounts.won],
          ['exited',   'Exited',    scopedCounts.exited],
          ['archived', 'Archived',  scopedCounts.archived],
        ].map(([k, label, n]) => {
          const active = tab === k;
          return (
            <button key={k} onClick={() => changeTab(k)} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
              ...(device.touch ? { flexShrink: 0, whiteSpace: 'nowrap' } : null),
            }}>
              {label}
              <span style={{ fontSize: 10.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{n != null ? n.toLocaleString() : '—'}</span>
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center', ...(device.touch ? { flexShrink: 0, paddingLeft: 12 } : null) }}>
          {/* Ownership scope. "Team" rather than "All" — this row already
              has an All tab meaning something different. */}
          <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {[
              ['mine', 'Mine', 'Only deals where you are the dealmaker'],
              ['team', 'Team', 'Everyone on your team'],
            ].map(([k, label, tip]) => (
              <button key={k} onClick={() => changeScope(k)} title={tip} style={{
                padding: '3px 9px', border: 'none', background: scope === k ? 'var(--surface)' : 'transparent',
                boxShadow: scope === k ? 'var(--shadow-sm)' : 'none',
                borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                color: scope === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', height: 22,
              }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {/* Deals grid vs the firm-grouped Buyers directory. "All types"
                is gone: shells are excluded server-side on every tab, so it
                had become a second Sellers button. */}
            {[
              ['seller_buyside', 'Deals',  typeCounts.seller_buyside],
              ['buyer_buyside',  'Buyers', typeCounts.buyer_buyside],
            ].map(([k, label, n]) => (
              <button key={k} onClick={() => setType(k)} style={{
                padding: '3px 9px', border: 'none', background: type === k ? 'var(--surface)' : 'transparent',
                boxShadow: type === k ? 'var(--shadow-sm)' : 'none',
                borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                color: type === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 5, height: 22,
              }}>
                {label}{n != null && <span style={{ fontSize: 10, color: 'var(--text-4)' }}>{n.toLocaleString()}</span>}
              </button>
            ))}
          </div>
          <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {[['table', <Icon.List size={12} />, 'Table'], ['kanban', <Icon.Kanban size={12} />, 'Board']].map(([k, ic, label]) => (
              <button key={k} onClick={() => setLayout(k)} style={{
                padding: '3px 9px', border: 'none', background: layout === k ? 'var(--surface)' : 'transparent',
                boxShadow: layout === k ? 'var(--shadow-sm)' : 'none',
                borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                color: layout === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 5, height: 22,
              }}>
                {ic}{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div ref={scrollRef} onScroll={(e) => { scrollTopRef.current = e.currentTarget.scrollTop; }} style={{ flex: 1, overflow: 'auto' }}>
        {type === 'buyer_buyside' ? (
          // Buyers tab: buyer-side engagements are one-per-deal and named after
          // the buyer, so a flat list repeats each firm. Group by firm instead
          // and expand to the deals they're a buyer on (seller names + pills).
          <BuyerFirmsView
            search={searchQ}
            onOpenCompany={setCompanyId ? (id) => { setView('company'); setCompanyId(id); } : null}
            onOpenDeal={(id) => { setView('deal'); setDealId(id); }}
          />
        ) : items === null ? (
          <SkeletonRows rows={10} variant="engagement" />
        ) : layout === 'table' ? (
          <>
            {tab === 'won' && window.ClosedDealsStrip && <window.ClosedDealsStrip />}
            <DealsTable
              rows={rows}
              stageBySlug={stageBySlug}
              stages={stageList || []}
              groupBy={groupBy}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
              onOpen={openDeal}
              onOpenCompany={openCompany}
              members={members}
              onSaveCell={saveEngagement}
              onStage={saveStage}
              fading={fading}
              onHold={saveHold}
              onExit={openExit}
              onWon={openWon}
              onNextAction={saveNextAction}
              selected={selected}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              columns={columns}
            />
            <window.HoldReasonPrompt
              open={!!holdPrompt}
              dealName={holdPrompt?.name || ''}
              reason={holdPrompt?.reason || null} cadence={holdPrompt?.cadence || null}
              onPick={async (patch) => {
                const id = holdPrompt?.id;
                setHoldPrompt(null);
                if (id) await saveEngagement(id, patch);
              }}
              onClose={() => setHoldPrompt(null)}
            />
            {window.ImportDrawer && (
              <window.ImportDrawer kind="deals" open={importing} onClose={() => setImporting(false)}
                onDone={() => { reload(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
            )}
            {window.BulkEditDrawer && (
              <window.BulkEditDrawer open={bulkEditing} kind="deals" ids={[...selected]}
                onClose={() => setBulkEditing(false)}
                onDone={() => { reload(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
            )}
            {window.ClosingDrawer && (
              <window.ClosingDrawer
                open={!!wonTarget}
                deal={wonTarget ? wonTarget.d : null}
                closing={null}
                onClose={() => setWonTarget(null)}
                onSubmit={saveWon}
              />
            )}
            <window.ExitDealDrawer
              open={!!exitTarget}
              deal={exitTarget || {}}
              exits={exitsFor(exitTarget)}
              onClose={() => setExitTarget(null)}
              onExit={saveExit}
            />
            {hasMore && (
              <div ref={sentinelRef}>
                <SkeletonRows rows={3} variant="engagement" />
              </div>
            )}
            {!hasMore && items.length > 0 && (
              <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--text-4)', textAlign: 'center' }}>
                {/* Loaded-vs-total is a pagination readout, so it has to name
                    the client-filtered count too — "6 of 6" under 2 visible
                    rows reads as a miscount rather than as "4 are filtered". */}
                · end of list · {rows.length !== items.length
                  ? `${rows.length.toLocaleString()} shown · ${items.length.toLocaleString()} of ${total.toLocaleString()} loaded`
                  : `${items.length.toLocaleString()} of ${total.toLocaleString()}`}
              </div>
            )}
          </>
        ) : (
          <DealsBoard rows={rows} stages={stageList} onOpen={(id) => { setView('deal'); setDealId(id); }}
            total={total} loaded={items ? items.length : 0} hasMore={hasMore} paging={paging}
            onLoadMore={() => fetchPage(page + 1, true)} />
        )}
      </div>

      {creating && (
        <NewEngagementDrawer
          stages={stageList || []}
          activeUser={activeUser}
          onClose={() => setCreating(false)}
          onCreated={(eng) => {
            setCreating(false);
            reload();
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            if (eng?.id) { setView('deal'); setDealId(eng.id); }
          }}
        />
      )}
    </div>
  );
};

// Stage-probability bar — visual companion to StageBadge. Width fills to
// `pct`, color matches the stage hue. Exited engagements render as a muted
// rose bar regardless of probability (the deal didn't progress, it died).
const ProgressBar = ({ pct, hue = 'slate', exited = false }) => {
  if (pct == null) {
    return <span style={{ fontSize: 11, color: 'var(--text-4)' }}>—</span>;
  }
  const clamped = Math.max(0, Math.min(100, pct));
  const fillHue = exited ? 'rose' : hue;
  // Resolve through the HUE token map (data.jsx) — 'blue' has no --blue-*
  // css vars, it aliases to the accent color. Interpolating the var name
  // directly renders an invisible fill for blue-hued stages (LOI, ss_ioi…).
  const fill = (HUE[fillHue] || HUE.slate).fg;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}
      title={`${clamped}% — pipeline probability`}>
      <div style={{
        flex: 1, height: 6, background: 'var(--surface-2)',
        border: '1px solid var(--border)', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          width: `${clamped}%`, height: '100%',
          background: fill,
          transition: 'width .25s',
        }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-2)', minWidth: 28, textAlign: 'right' }}
        className="num">
        {Math.round(clamped)}%
      </span>
    </div>
  );
};

// Hold sublist inside the stage picker (Zach, 2026-09): parking a deal is a
// pick in the same dropdown as the stage, but it edits on_hold, not stage_id
// — the stage stays where it is. Values carry a prefix so the cell can tell
// them from stage ids; `group` renders them under an <optgroup> after the
// real stages. A parked row also gets the way back out (and the other
// priority), otherwise the grid could park a deal but never unpark it.
const HOLD_OPT_PREFIX = 'hold:';
const holdOptsFor = (d) => [
  ...(d.onHold !== 'high' ? [{ value: `${HOLD_OPT_PREFIX}high`, label: 'On hold · high priority', group: 'On hold' }] : []),
  ...(d.onHold !== 'low'  ? [{ value: `${HOLD_OPT_PREFIX}low`,  label: 'On hold · low priority',  group: 'On hold' }] : []),
  ...(d.onHold            ? [{ value: HOLD_OPT_PREFIX,           label: 'Take off hold',           group: 'On hold' }] : []),
];

const holdLabel = (onHold, reason) => `On hold · ${onHold}${reason ? ` · ${prettyLabel(reason)}` : ''}`;

// Exit pick, same dropdown. An exit needs a reason (the CURRENT stage's exit
// definitions) and takes an optional note, so the pick opens the deal page's
// exit drawer for the row instead of PATCHing a bare exit_id. One entry under
// its own group so it can't be mistaken for a stage or a hold priority.
const EXIT_OPT_VALUE = 'exit:';
const EXIT_OPTS = [{ value: EXIT_OPT_VALUE, label: 'Exit deal…', group: 'Exit' }];

const StageBadge = ({ slug, name, onHold, onHoldReason, exitName }) => {
  const hue = STAGE_HUE[slug] || 'slate';
  if (exitName) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Pill hue="rose" sz="sm">Exited</Pill>
        <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{exitName}</span>
      </span>
    );
  }
  if (onHold) {
    // Stacked, hold on top. Side by side the pair only fit when the stage
    // name was short — "Director/Seller Call" pushed the hold pill past the
    // cell's single-line ellipsis, so the On hold tab showed no hold at all.
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '2px 0' }}>
        <Pill hue="amber" sz="sm">{holdLabel(onHold, onHoldReason)}</Pill>
        <Pill hue={hue} dot sz="sm">{name}</Pill>
      </span>
    );
  }
  return <Pill hue={hue} dot sz="sm">{name || slug}</Pill>;
};

// Interested-buyer badges. Interest runs cold → very hot; NDA tracks the paper
// trail. Hues chosen so "hot/signed" read positive and "passed" reads dead.
const INTEREST_HUE = { very_hot: 'green', hot: 'teal', warm: 'amber', cold: 'slate', passed: 'rose' };
const NDA_HUE      = { signed: 'green', pending: 'amber', sent: 'amber', redlined: 'amber', declined: 'rose', not_sent: 'slate' };
const prettyLabel  = (s) => (s || '').replace(/_/g, ' ');

// Pull the most-recent date token out of a free-text note and flatten it to a
// single line. The team stamps updates like "05-15-26 …" (also 5/1/25, 11.1.23,
// "on 05-09-2026"); we surface the latest as a compact "Updated" column and
// strip a leading date from the shown text so it isn't repeated.
const _DATE_RE = /(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/g;
const noteMeta = (note) => {
  if (!note) return { date: '', text: '' };
  const flat = note.replace(/\s+/g, ' ').trim();
  let m, bestKey = -1, bestRaw = '';
  _DATE_RE.lastIndex = 0;
  while ((m = _DATE_RE.exec(flat))) {
    let y = Number(m[3]); if (y < 100) y += 2000;
    const key = y * 10000 + Number(m[1]) * 100 + Number(m[2]);
    if (key > bestKey) { bestKey = key; bestRaw = m[0]; }
  }
  const text = flat.replace(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})[\s:.-]*/, '');
  return { date: bestRaw, text };
};

// Expanded panel under a seller row: the buyers introduced to / interested in
// this deal, from /api/buyers/for-engagement (buyer_engagements). Lazy-loaded
// on first expand and cached by the parent DealsTable. Buyers carry almost no
// structured fields in DealCloud (no per-buyer fees/progress) — the signal is
// the derived status pill + the free-text note — so this is a purpose-built
// mini-table, not the seller column grid.
const InterestedBuyers = ({ state, onOpenCompany }) => {
  const wrap = (children, color) => (
    <div style={{ padding: '8px 14px 12px 60px', background: 'var(--surface-2)', fontSize: 11.5, color }}>{children}</div>
  );
  if (!state || state.loading) return wrap('Loading buyers…', 'var(--text-4)');
  if (state.error) return wrap('Couldn’t load buyers.', 'var(--rose-fg)');
  const items = state.items || [];
  if (!items.length) return wrap('No interested buyers recorded.', 'var(--text-4)');
  const COLS = { buyer: 220, status: 130, updated: 74 };
  const Cell = ({ w, children, style, ...rest }) => (
    <div style={{ width: w, flexShrink: 0, ...style }} {...rest}>{children}</div>
  );
  return (
    <div style={{ padding: '8px 14px 12px 60px', background: 'var(--surface-2)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, marginBottom: 6 }}>
        {items.length} interested buyer{items.length > 1 ? 's' : ''}
      </div>
      {/* Column header for the buyer mini-table. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '0 9px 4px',
        fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-4)',
      }}>
        <Cell w={COLS.buyer}>Buyer</Cell>
        <Cell w={COLS.status}>Status</Cell>
        <Cell w={COLS.updated}>Updated</Cell>
        <div style={{ flex: 1, minWidth: 0 }}>Last note</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {items.map((b) => {
          const meta = noteMeta(b.notes);
          return (
            <div key={b.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '5px 9px',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12,
            }}>
              <Cell w={COLS.buyer} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <button onClick={() => onOpenCompany && onOpenCompany(b.buyer_company_id)}
                  disabled={!onOpenCompany}
                  style={{
                    background: 'transparent', border: 'none', padding: 0,
                    cursor: onOpenCompany ? 'pointer' : 'default',
                    fontSize: 12, fontWeight: 600, color: 'var(--accent-text)', textAlign: 'left',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                  }}>
                  {b.buyer_name || '—'}
                </button>
                {b.buyer_total_deals > 1 && (
                  <span title={`On ${b.buyer_total_deals} deals`} style={{ flexShrink: 0, fontSize: 10, color: 'var(--text-4)' }}>·{b.buyer_total_deals}</span>
                )}
              </Cell>
              {/* Status: derived from the notes (NDA signed / passed). Only shown
                  when known — DealCloud has no reliable structured status field. */}
              <Cell w={COLS.status} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {b.nda_status && b.nda_status !== 'not_sent' && (
                  <Pill hue={NDA_HUE[b.nda_status] || 'slate'} sz="sm">NDA {prettyLabel(b.nda_status)}</Pill>
                )}
                {b.interest_level && <Pill hue={INTEREST_HUE[b.interest_level] || 'slate'} sz="sm">{prettyLabel(b.interest_level)}</Pill>}
                {b.loi_submitted && <Pill hue="blue" sz="sm">LOI</Pill>}
              </Cell>
              <Cell w={COLS.updated} style={{ fontSize: 11, color: 'var(--text-3)' }} className="num">
                {meta.date || '—'}
              </Cell>
              <div title={b.notes || ''} style={{
                flex: 1, minWidth: 0, color: 'var(--text-3)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{meta.text || '—'}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Buyer-deal status buckets for the expanded-panel tabs. The buyer-specific
// pass (interest_level) outranks the seller deal's stage: a deal can be Won
// overall while THIS buyer passed on it — for this firm it belongs in Passed.
const buyerDealStatus = (d) =>
  d.interest_level === 'passed' ? 'passed' : d.stage_slug === 'won' ? 'won' : 'active';

// Expanded panel under a buyer-firm row: the seller deals this firm is a buyer
// on, from /api/buyers/for-company (already sorted newest next-action first).
// Mirror of InterestedBuyers, but the primary link is the seller/deal (not the
// buyer), and the deals are split into Active / Won / Passed tabs.
const BuyerFirmDeals = ({ state, onOpenDeal }) => {
  const [tabPick, setTabPick] = React.useState(null);
  const wrap = (children, color) => (
    <div style={{ padding: '8px 14px 12px 60px', background: 'var(--surface-2)', fontSize: 11.5, color }}>{children}</div>
  );
  if (!state || state.loading) return wrap('Loading deals…', 'var(--text-4)');
  if (state.error) return wrap('Couldn’t load deals.', 'var(--rose-fg)');
  const items = state.items || [];
  if (!items.length) return wrap('No deals recorded.', 'var(--text-4)');
  const counts = { all: items.length, active: 0, won: 0, passed: 0 };
  items.forEach((d) => { counts[buyerDealStatus(d)] += 1; });
  // Land on Active by default; fall back to All when the firm has none.
  const tab = tabPick || (counts.active > 0 ? 'active' : 'all');
  const shown = tab === 'all' ? items : items.filter((d) => buyerDealStatus(d) === tab);
  return (
    <div style={{ padding: '8px 14px 12px 60px', background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 6 }}>
        {[
          ['active', 'Active', counts.active],
          ['won',    'Won',    counts.won],
          ['passed', 'Passed', counts.passed],
          ['all',    'All',    counts.all],
        ].map(([k, label, n]) => {
          const on = tab === k;
          return (
            <button key={k} onClick={() => setTabPick(k)} style={{
              padding: '2px 8px', border: 'none', borderRadius: 5, cursor: 'pointer',
              background: on ? 'var(--surface)' : 'transparent',
              boxShadow: on ? 'var(--shadow-sm)' : 'none',
              fontSize: 11, fontWeight: on ? 600 : 500,
              color: on ? 'var(--text)' : 'var(--text-3)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {label}<span style={{ fontSize: 10, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
            </button>
          );
        })}
      </div>
      {/* Column header for the deal mini-table. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '0 9px 4px',
        fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-4)',
      }}>
        <div style={{ width: 220, flexShrink: 0 }}>Deal</div>
        <div style={{ width: 130, flexShrink: 0 }}>Status</div>
        <div style={{ width: 150, flexShrink: 0 }}>Stage</div>
        <div style={{ width: 74, flexShrink: 0 }}>Next action</div>
        <div style={{ flex: 1, minWidth: 0 }}>Last activity</div>
      </div>
      {!shown.length ? (
        <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '2px 9px' }}>No {tab} deals.</div>
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {shown.map((d) => {
          const passNote = noteMeta(d.notes).text;
          // Passed rows lead with the buyer's pass note (the why); live rows
          // lead with the deal's DealCloud next-action note (the what's-next).
          const activity = (d.interest_level === 'passed' && passNote) || d.next_action_notes || passNote;
          const tip = [d.next_action_notes, d.notes].filter(Boolean).join('\n—\n');
          return (
            <div key={d.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '5px 9px',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12,
              opacity: d.engagement_archived ? 0.6 : 1,
            }}>
              <button onClick={() => onOpenDeal && onOpenDeal(d.engagement_id)}
                style={{
                  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: 'var(--accent-text)', textAlign: 'left',
                  flex: '0 0 220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                {d.seller_company_name || d.engagement_name || '—'}
              </button>
              <div style={{ width: 130, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                {d.nda_status && d.nda_status !== 'not_sent' && (
                  <Pill hue={NDA_HUE[d.nda_status] || 'slate'} sz="sm">NDA {prettyLabel(d.nda_status)}</Pill>
                )}
                {d.interest_level && <Pill hue={INTEREST_HUE[d.interest_level] || 'slate'} sz="sm">{prettyLabel(d.interest_level)}</Pill>}
                {d.loi_submitted && <Pill hue="blue" sz="sm">LOI</Pill>}
              </div>
              <div style={{ width: 150, flexShrink: 0, fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.stage_name || '—'}
              </div>
              <div style={{ width: 74, flexShrink: 0, fontSize: 11, color: 'var(--text-3)' }} className="num">
                {d.next_action_date && window.fmtDate ? window.fmtDate(d.next_action_date) : '—'}
              </div>
              <div title={tip} style={{ flex: 1, minWidth: 0, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activity || '—'}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

// Grouped Buyers tab: one row per buyer firm (from /api/buyers/firms, aggregated
// over buyer_engagements so counts match the expand), expanding to the deals
// they're a buyer on. Lazy-loads each firm's deals on first expand and caches.
const BuyerFirmsView = ({ search, onOpenCompany, onOpenDeal }) => {
  const [firms, setFirms] = React.useState(null);
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [dealsByFirm, setDealsByFirm] = React.useState({});
  const loadedRef = React.useRef({});

  React.useEffect(() => {
    let alive = true;
    setFirms(null);
    window.AtlasApi.buyerFirms(search ? { q: search } : {})
      .then((r) => { if (alive) setFirms((r && r.items) || []); })
      .catch(() => { if (alive) setFirms([]); });
    return () => { alive = false; };
  }, [search]);

  const toggle = React.useCallback((cid) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(cid)) n.delete(cid); else n.add(cid);
      return n;
    });
    if (!loadedRef.current[cid]) {
      loadedRef.current[cid] = true;
      setDealsByFirm((p) => ({ ...p, [cid]: { loading: true, items: [] } }));
      window.AtlasApi.buyerEngagementsForCompany(cid)
        .then((r) => setDealsByFirm((p) => ({ ...p, [cid]: { loading: false, items: (r && r.items) || [] } })))
        .catch(() => setDealsByFirm((p) => ({ ...p, [cid]: { loading: false, items: [], error: true } })));
    }
  }, []);

  if (firms === null) return <SkeletonRows rows={10} variant="engagement" />;
  if (!firms.length) {
    return <div style={{ padding: 16, fontSize: 12, color: 'var(--text-3)' }}>No buyer firms{search ? ` matching “${search}”` : ''}.</div>;
  }

  return (
    <div style={{ minWidth: 'max-content' }}>
      <div style={{
        display: 'flex', alignItems: 'center', height: 30, borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)', position: 'sticky', top: 0, zIndex: 1, fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
      }}>
        <div style={{ width: 360, padding: '0 10px' }}>Buyer firm</div>
        <div style={{ width: 80, padding: '0 10px', textAlign: 'right' }}>Deals</div>
        <div style={{ width: 190, padding: '0 10px' }}>Status</div>
        <div style={{ width: 120, padding: '0 10px' }}>Next action</div>
      </div>
      {firms.map((f) => {
        const isEx = expanded.has(f.company_id);
        return (
          <React.Fragment key={f.company_id}>
            <div onClick={() => toggle(f.company_id)}
              style={{ display: 'flex', alignItems: 'center', minHeight: 38, borderBottom: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', transition: 'background .1s' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
            >
              <div style={{ width: 360, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-flex', width: 14, flexShrink: 0, color: 'var(--text-3)', transition: 'transform .12s', transform: isEx ? 'rotate(90deg)' : 'none' }}>
                  <Icon.ChevronR size={12} />
                </span>
                <Logo name={f.firm} color={window.hashColor(f.firm)} size={20} />
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenCompany && onOpenCompany(f.company_id); }}
                  disabled={!onOpenCompany}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: onOpenCompany ? 'pointer' : 'default', fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.firm}
                </button>
              </div>
              <div style={{ width: 80, padding: '0 10px', textAlign: 'right', fontSize: 12 }} className="num">{f.deals}</div>
              <div style={{ width: 190, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                {f.won > 0 && <Pill hue="green" sz="sm">{f.won} won</Pill>}
                {f.nda_signed > 0 && <Pill hue="teal" sz="sm">{f.nda_signed} NDA</Pill>}
                {f.passed > 0 && <Pill hue="rose" sz="sm">{f.passed} passed</Pill>}
              </div>
              <div style={{ width: 120, padding: '0 10px', fontSize: 11.5, color: 'var(--text-3)' }}>
                {f.last_next_action && window.fmtDate ? window.fmtDate(f.last_next_action) : '—'}
              </div>
            </div>
            {isEx && (
              <div style={{ borderBottom: '1px solid var(--border)' }}>
                <BuyerFirmDeals state={dealsByFirm[f.company_id]} onOpenDeal={onOpenDeal} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Next action cell ────────────────────────────────────────────────────────
// Shows the deal's current next action (date + note) with a "+" affordance.
// Adding a new one goes through onSave → a "Next Action" note: the server
// replaces the engagement's next_action_notes/next_action_date and the old
// value stays in the deal's note history.
const NextActionPopover = ({ anchor, onSave, onClose }) => {
  const [body, setBody]     = React.useState('');
  const [date, setDate]     = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);
  const save = async () => {
    if (!body.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({ body: body.trim(), date: date || null });
      pushToast({ type: 'success', title: 'Next action updated', body: 'Previous one kept in Notes.' });
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      setSaving(false);
    }
  };
  // Anchored to the "+" button, clamped to the viewport; flips above the
  // anchor near the bottom edge (same fixed+portal approach as Combobox).
  const W = 300, H = 210;
  const left = Math.max(8, Math.min(anchor.left - W + anchor.width, window.innerWidth - W - 8));
  const up = anchor.bottom + H + 8 > window.innerHeight;
  return ReactDOM.createPortal(
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
      }}
      style={{
        position: 'fixed', left, width: W, zIndex: 400,
        ...(up ? { bottom: window.innerHeight - anchor.top + 4 } : { top: anchor.bottom + 4 }),
        background: 'var(--surface)', border: '1px solid var(--border-strong)',
        borderRadius: 7, boxShadow: 'var(--shadow-lg)', padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <textarea
        autoFocus
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="What happens next on this deal?"
        rows={3}
        style={{
          width: '100%', padding: '6px 8px', fontSize: 12.5, resize: 'vertical',
          border: '1px solid var(--border)', borderRadius: 5,
          background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Due</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{
            flex: 1, padding: '4px 7px', fontSize: 12,
            border: '1px solid var(--border)', borderRadius: 5,
            background: 'var(--surface-2)', color: 'var(--text)',
          }}
        />
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', lineHeight: 1.4 }}>
        Replaces the deal’s current next action (also in DealCloud); the old one stays in Notes.
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <Btn size="xs" variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn size="xs" variant="accent" disabled={!body.trim() || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Btn>
      </div>
    </div>,
    document.body,
  );
};

// Read view + "log a new one" composer for an engagement's next action.
// Takes plain values (not a row shape) so the analytics/dashboard tables can
// render the same cell — it is exported as window.NextActionCell. `onSave`
// receives {body, date} and owns the write: a next action is a NOTE, and the
// server swaps next_action_notes/date off it, so callers POST a note rather
// than PATCHing the two fields.
// `showDate` off suppresses the due-date line for tables that already carry a
// separate Next Action date column (the DealCloud replica grids) — the date is
// still written by the composer, it just isn't printed twice side by side.
const NextActionCell = ({ note, date, onSave, showDate = true }) => {
  const [anchor, setAnchor] = React.useState(null);
  const btnRef = React.useRef(null);
  // Compare against the LOCAL calendar date — next_action_date is a bare
  // date, and slicing toISOString() (UTC) would call a task due today
  // "overdue" for anyone working after 7pm Central.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const overdue = date && date < today;
  const dueToday = date === today;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <div
        style={{ flex: 1, minWidth: 0 }}
        title={[date ? window.fmtDate(date) : null, note].filter(Boolean).join(' — ') || undefined}
      >
        {(note || (date && showDate)) ? (
          <>
            {date && showDate && (
              <div style={{ fontSize: 10.5, fontWeight: 500, color: overdue ? 'var(--rose-fg)' : dueToday ? 'var(--amber-fg)' : 'var(--text-3)' }}>
                {window.fmtDate(date)}{overdue ? ' · overdue' : dueToday ? ' · today' : ''}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {note || '—'}
            </div>
          </>
        ) : (
          <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>—</span>
        )}
      </div>
      <button
        ref={btnRef}
        onClick={() => setAnchor(btnRef.current.getBoundingClientRect())}
        title={note || date ? 'Log a new next action (replaces this one)' : 'Add a next action'}
        style={{
          flexShrink: 0, width: 18, height: 18, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          border: '1px solid var(--border)', borderRadius: 5,
          background: 'var(--surface-2)', color: 'var(--text-3)', padding: 0,
        }}
      >
        <Icon.Plus size={10} />
      </button>
      {anchor && <NextActionPopover anchor={anchor} onSave={onSave} onClose={() => setAnchor(null)} />}
    </div>
  );
};

// Most recent note linked to the deal (or its company): when + what.
const LatestNoteCell = ({ d, w }) => {
  if (!d.latestNoteAt) {
    return <div style={{ width: w, padding: '0 10px', fontSize: 11.5, color: 'var(--text-4)' }}>—</div>;
  }
  const preview = (d.latestNotePreview || '').replace(/\s+/g, ' ').trim();
  const label = d.latestNoteTitle || preview || '(no text)';
  const tip = [
    `${window.fmtDate(d.latestNoteAt)}${d.latestNoteSubtype ? ` · ${d.latestNoteSubtype}` : ''}`,
    d.latestNoteTitle,
    preview,
  ].filter(Boolean).join('\n');
  return (
    <div style={{ width: w, padding: '0 10px', minWidth: 0 }} title={tip}>
      <div style={{ fontSize: 10.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {window.relativeTime(d.latestNoteAt)}{d.latestNoteSubtype ? ` · ${d.latestNoteSubtype}` : ''}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </div>
    </div>
  );
};

// Soonest upcoming calendar event on the deal's company: when + what, with
// who's on the invite in the tooltip. Blank when nothing is booked.
// All-day events are stored at 00:00 UTC — read them as that calendar day,
// not as the local evening before.
const meetingStart = (iso, allDay) => {
  const t = new Date(iso);
  return allDay && !Number.isNaN(t.getTime()) ? new Date(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()) : t;
};
const fmtMeetingWhen = (iso, allDay) => {
  const t = meetingStart(iso, allDay);
  if (Number.isNaN(t.getTime())) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(t); day.setHours(0, 0, 0, 0);
  const days = Math.round((day - today) / 86400000);
  const date = days === 0 ? 'Today' : days === 1 ? 'Tomorrow'
    : t.toLocaleDateString(undefined, days < 7
      ? { weekday: 'short' }
      : { weekday: 'short', month: 'short', day: 'numeric', ...(t.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : null) });
  if (allDay) return date;
  return `${date} · ${t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

const NextMeetingCell = ({ d, w }) => {
  if (!d.nextMeetingAt) return <div style={{ width: w, padding: '0 10px' }} />;
  const when = fmtMeetingWhen(d.nextMeetingAt, d.nextMeetingAllDay);
  const full = meetingStart(d.nextMeetingAt, d.nextMeetingAllDay).toLocaleString(undefined, d.nextMeetingAllDay
    ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
    : { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const tip = [full, d.nextMeetingTitle, ...(d.nextMeetingAttendees || [])].filter(Boolean).join('\n');
  return (
    <div style={{ width: w, padding: '0 10px', minWidth: 0 }} title={tip}>
      <div style={{ fontSize: 10.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} className="num">
        {when}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.nextMeetingTitle || '(untitled meeting)'}
      </div>
    </div>
  );
};

// "Group" button + menu (None / Stage / Dealmaker / Industry). Same popover
// mechanics as ExportMenu: outside-click and Escape close it.
const GroupMenu = ({ groupBy, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const pick = (g) => { setOpen(false); onChange(g); };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Btn size="sm" variant={groupBy ? 'accent' : 'outline'} icon={<Icon.Layers size={12} />}
        onClick={() => setOpen((v) => !v)}
        title="Group rows under section headers">
        {groupBy ? `Group: ${GROUP_SPECS[groupBy].label}` : 'Group'}
      </Btn>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 30, right: 0, zIndex: 60, minWidth: 150,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
        }}>
          {[['', 'None'], ...Object.entries(GROUP_SPECS).map(([k, s]) => [k, s.label])].map(([k, label]) => {
            const on = groupBy === k;
            return (
              <button key={k || 'none'} onClick={() => pick(k)} style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                padding: '8px 12px', textAlign: 'left', border: 'none', cursor: 'pointer',
                background: 'var(--surface)', fontSize: 12.5,
                fontWeight: on ? 600 : 400,
                color: on ? 'var(--accent-text)' : 'var(--text)',
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
              >
                <span style={{ flex: 1 }}>{label}</span>
                {on && <Icon.Check size={11} color="var(--accent-text)" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Revenue/EBITDA cell: DealCloud's engagement-level Reported* fields are
// sparse — for a third of live deals the number lives on the linked Company
// instead. Fall back to the company estimate, marked with a muted "~" and a
// tooltip so it reads as an estimate via Company (the detail view's
// "via Company" treatment, sized for a grid cell).
// Editing writes the ENGAGEMENT-level figure (reported_revenue /
// reported_ebitda) — never the company estimate, which is shared by every
// other engagement on that company. So the editor opens on `reported` (blank
// when the shown number is the company fallback) and typing a value replaces
// the "~" with a hard engagement figure.
const EstMoneyCell = ({ reported, companyEst, w, editable = false, locked = false, onSave }) => {
  const EditableCell = window.EditableCell;
  const viaCompany = reported == null && companyEst != null;
  const title = viaCompany
    ? `Company estimate — no engagement-level figure${editable ? '. Click to set one on this engagement.' : ''}`
    : undefined;
  const readView = (
    <span className="num" style={{ color: 'var(--text-3)' }}>
      {viaCompany && <span style={{ color: 'var(--text-4)' }}>~</span>}
      {fmtMoney(reported ?? companyEst)}
    </span>
  );
  if (!editable) {
    return (
      <div title={title} style={{ width: w, padding: '0 10px', textAlign: 'right', fontSize: 12, color: 'var(--text-3)' }} className="num">
        {readView}
      </div>
    );
  }
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      title={title}
      style={{ ...window.EDIT_CELL_WRAP, width: w, padding: '0 10px', fontSize: 12, justifyContent: 'flex-end' }}
    >
      <EditableCell type="currency" fill align="right" value={reported} displayAs={readView} locked={locked} onSave={onSave} />
    </div>
  );
};

// Contact cell: the company's primary contact as a link, plus a hover pencil
// that swaps in a searchable picker of that company's contacts. Picking one
// PATCHes primary_contact_id — a per-engagement pin that beats the
// server-derived best match — and "Auto" clears the pin. The name stays a
// plain link; the pencil is the only edit affordance (the link+pencil hybrid
// EditableCell uses for its link types).
// One cell component for the Contact, Contact email and Contact phone
// columns: `field` picks what to display, the picker underneath is shared.
// Picking a contact in any of the three re-pins the engagement, so the other
// two follow. When the company has more than one contact the chevron stays
// visible (not hover-only) — that is the "which contact?" affordance.
const ContactCell = ({ d, editable, onSave, field = 'name' }) => {
  const [editing, setEditing] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const rootRef = React.useRef(null);

  // Exit edit mode on a true click-away. The Combobox menu is portaled to
  // <body>, so whitelist it the way FilterPopover does.
  React.useEffect(() => {
    if (!editing) return;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-combobox-menu]')) return;
      setEditing(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [editing]);

  const companyId = d.target || d._raw?.company_id || null;
  const loadOptions = React.useCallback((q) => (
    window.AtlasApi.listContacts({ company_id: companyId, ...(q ? { q } : null), limit: 50 })
      .then((r) => (r.items || []).map((c) => ({
        value: c.id,
        label: c.full_name || '(unnamed)',
        hint: c.title || c.email || undefined,
      })))
  ), [companyId]);

  const commit = async (next) => {   // next = contact id, or null for Auto
    setEditing(false);
    const current = d.contactPinned ? d.contactId : null;
    if (next === current) return;
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div ref={rootRef} onClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
        <window.Combobox
          autoOpen
          value={d.contactPinned ? (d.contactId || '') : ''}
          options={[{ value: '', label: 'Auto — best match' }]}
          loadOptions={loadOptions}
          placeholder="Search contacts…"
          onChange={(v) => commit(v.value || null)}
          style={{ width: '100%', fontSize: 12 }}
        />
      </div>
    );
  }

  return (
    <span
      ref={rootRef}
      onMouseEnter={window.hoverOnly(() => setHovered(true))}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}
    >
      {(() => {
        const linkStyle = {
          fontSize: 12, color: 'var(--accent-text)', textDecoration: 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          minWidth: 0, maxWidth: '100%',
        };
        const hover = {
          onMouseOver: (e) => { e.currentTarget.style.textDecoration = 'underline'; },
          onMouseOut:  (e) => { e.currentTarget.style.textDecoration = 'none'; },
        };
        if (field === 'email') {
          const email = (d.contactEmail || '').trim();
          if (!d.contactId || !email) return <span style={{ fontSize: 12, color: 'var(--text-4)' }}>—</span>;
          return (
            <>
              <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()}
                 title={`Email ${d.contactName} (${email})`} style={linkStyle} {...hover}>{email}</a>
              <window.CopyIconBtn text={email} />
            </>
          );
        }
        if (field === 'phone') {
          // DealCloud phones arrive as free text — "(916) 468-8893    (Work)",
          // or junk like ".    (Work)". Collapse the spacing for display and
          // treat a value with no digits as empty rather than a dead tel: link.
          const phone = (d.contactPhone || '').replace(/\s+/g, ' ').trim();
          const digits = phone.replace(/[^\d+]/g, '');
          if (!d.contactId || !/\d/.test(digits)) return <span style={{ fontSize: 12, color: 'var(--text-4)' }}>—</span>;
          return (
            <>
              <a href={`tel:${digits}`} onClick={(e) => e.stopPropagation()}
                 title={`Call ${d.contactName} (${phone})`} style={linkStyle} {...hover}>{phone}</a>
              <window.CopyIconBtn text={phone} />
            </>
          );
        }
        if (!d.contactId) return <span style={{ fontSize: 12, color: 'var(--text-4)' }}>—</span>;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToContact(d.contactId); }}
            title={`Open ${d.contactName}${d.contactTitle ? ` — ${d.contactTitle}` : ''}${d.contactPinned ? ' (pinned on this engagement)' : ''}`}
            style={{ ...linkStyle, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            {d.contactName}
          </button>
        );
      })()}
      {saving ? (
        <span style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0 }}>saving…</span>
      ) : (editable && companyId && (hovered || d.contactCount > 1 || window.AtlasDevice?.touch) && (
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          title={d.contactCount > 1
            ? `${d.contactCount} contacts at this company — choose which one shows here`
            : (d.contactPinned ? 'Change the pinned contact, or reset to auto' : 'Pin a different contact for this engagement')}
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}
        >
          {d.contactCount > 1
            ? <Icon.ChevronD size={10} color={hovered ? 'var(--text-2)' : 'var(--text-4)'} />
            : <Icon.Edit size={10} color="var(--text-4)" />}
        </button>
      ))}
    </span>
  );
};

// ────────────────────────────────────────────────────────────────────────
// The engagement column catalog — ONE definition per column, covering its
// label, its width-fitting sample, and its cell. This used to be three lists
// (a fitColWidths spec, a header array, and hand-written body cells in
// matching order) that only agreed because someone kept them in sync by
// hand; reordering a column meant editing all three. Now the header and the
// body both map over the same resolved list, so they can't drift.
//
// `def: false` marks a column that ships hidden — available from the Columns
// menu, off by default. Those are the ones that used to require a code change
// to see at all. Sorting for every key lives in SORT_EXTRACT above.
//
// render(d, ctx, col) must size its own outer element to col.w — the row is a
// flexbox and a cell that ignores its width breaks the header alignment.
const ENGAGEMENT_COLUMNS = [
  {
    key: 'name', label: 'Engagement', accessor: (d) => d.targetName || d.name, extra: 34,
    fixed: true,   // row identity + the expand-to-buyers chevron live here
    xget: (d) => d.name || '',
    xextra: [{ key: 'company', label: 'Company', get: (d) => d.targetName || '' }],
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid var(--border)' }}>
        {d.buyerCount > 0 ? (
          <button
            onClick={(e) => { e.stopPropagation(); ctx.toggleExpand(d.id); }}
            title={`${d.buyerCount} interested buyer${d.buyerCount > 1 ? 's' : ''}`}
            style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              width: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-3)',
            }}>
            <span style={{
              display: 'inline-flex', transition: 'transform .12s',
              transform: ctx.expanded.has(d.id) ? 'rotate(90deg)' : 'none',
            }}>
              <Icon.ChevronR size={12} />
            </span>
          </button>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} />
        )}
        <Logo name={d.targetName || d.name} color={window.hashColor(d.targetName || d.name)} size={20} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
          {/* Second line: when the engagement name is identical to the
              target company name (the common case for sellsides), the
              "Acme · Acme" duplication is just noise. Show a small
              "View Company →" link instead. When names differ (rebrands,
              buyer-side deals named after the firm) we still show the
              company name as a clickable link. */}
          {d.target ? (
            <button
              onClick={(e) => { e.stopPropagation(); ctx.onOpenCompany && ctx.onOpenCompany(d.target); }}
              disabled={!ctx.onOpenCompany}
              style={{
                background: 'transparent', border: 'none', padding: 0, marginTop: 1,
                cursor: ctx.onOpenCompany ? 'pointer' : 'default',
                fontSize: 11, color: 'var(--accent-text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                textAlign: 'left',
              }}
            >
              {d.targetName && d.targetName !== d.name
                ? <>{d.targetName} <span style={{ opacity: 0.6 }}>→</span></>
                : <>View company <span style={{ opacity: 0.6 }}>→</span></>}
            </button>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 1 }}>—</div>
          )}
        </div>
      </div>
    ),
  },
  {
    // Lead source (142): where the lead first came from, inherited from the
    // company. Ships hidden — opt in from the Columns menu.
    key: 'leadSource', label: 'Lead source', group: 'Company', def: false,
    desc: 'Where this lead first came from — a workshop, a referral partner, inbound… Edit it on the deal or company record.',
    accessor: (d) => (d.leadSource === '—' ? '' : (d.leadSource || '')),
    xget: (d) => (d.leadSource === '—' ? '' : (d.leadSource || '')),
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', overflow: 'hidden' }} title={d.leadEvent || undefined}>
        {d.leadSourceId
          ? <window.LeadSourcePill name={d.leadSource} group={d.leadSourceGroup} />
          : <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>—</span>}
      </div>
    ),
  },
  {
    // The seller company's website. Off by default — it's a lookup onto the
    // linked company, not deal data — but when switched on it belongs right
    // next to the company it describes, not tacked on past the money columns.
    key: 'website', label: 'Website', group: 'Company', def: false,
    desc: "The linked company's website. Edit it on the company record.",
    accessor: (d) => d.companyDomain || '',
    xget: (d) => d.companyDomain || '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {d.companyDomain ? (
          <a href={`https://${d.companyDomain}`} target="_blank" rel="noopener noreferrer"
             onClick={(e) => e.stopPropagation()}
             title={`Open ${d.companyDomain} in a new tab`}
             style={{ fontSize: 11.5, color: 'var(--accent-text)', textDecoration: 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
             onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
             onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >{d.companyDomain}</a>
        ) : (
          <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>—</span>
        )}
      </div>
    ),
  },
  {
    key: 'stage', label: 'Stage', extra: 18,
    // Width fits the wider of the two stacked pills — the hold pill carries
    // priority + delay type and is usually the longer one.
    accessor: (d) => {
      const stage = d.stageName || d.stage || '';
      const hold = d.onHold ? holdLabel(d.onHold, d.onHoldReason) : '';
      return hold.length > stage.length ? hold : stage;
    },
    xget: (d) => d.stageName || d.stage || '',
    xextra: [
      { key: 'on_hold', label: 'On hold', get: (d) => d.onHold || '' },
      { key: 'exit',    label: 'Exit',    get: (d) => d.exitName || '' },
    ],
    // Stage is editable in place (the grid's most-asked-for edit — moving a
    // deal shouldn't cost a round trip through the detail page). Exited deals
    // stay read-only: reopening is the detail view's flow (it clears exit_id +
    // the exit stamp), and a bare stage_id PATCH would leave the row exited on
    // a live stage. Buyer shells carry DC's own funnel — never Atlas-editable.
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', borderRight: '1px solid var(--border)' }}>
        <window.EditableCell
          type="select" fill
          value={d._raw?.stage_id || null}
          options={[...ctx.stageOptsFor(d.type), ...(ctx.onHold ? holdOptsFor(d) : []), ...(ctx.onExit ? EXIT_OPTS : [])]}
          displayAs={<StageBadge slug={d.stage} name={ctx.stageBySlug[d.stage]?.name || d.stageName} onHold={d.onHold} onHoldReason={d.onHoldReason} exitName={d.exitName} />}
          editable={ctx.editable && ctx.stages.length > 0 && !d.exitName && d.type !== 'buyer_buyside'}
          locked={ctx.dcLockedE(d, 'stage_id')}
          onSave={(v) => (v === EXIT_OPT_VALUE
            ? ctx.onExit(d)
            : String(v).startsWith(HOLD_OPT_PREFIX)
              ? ctx.onHold(d, String(v).slice(HOLD_OPT_PREFIX.length) || null)
              : (ctx.onWon && window.isWonSlug && window.isWonSlug(ctx.stages.find((st) => st.id === v)?.slug))
                ? ctx.onWon(d, v)
                : ctx.onStage ? ctx.onStage(d, v) : ctx.onSaveCell(d.id, { stage_id: v }))}
        />
      </div>
    ),
  },
  {
    key: 'progress', label: 'Progress', extra: 40,   // progress bar (no text)
    filterKind: 'number',   // Filter popover compares the stage probability (0–100)
    xget: (d) => d.probability ?? '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', borderRight: '1px solid var(--border)' }}>
        <ProgressBar pct={d.probability} hue={STAGE_HUE[d.stage] || 'slate'} exited={Boolean(d.exitName)} />
      </div>
    ),
  },
  {
    // Long free text — measure a slice so one verbose note doesn't blow the
    // column out to the cap; extra covers the "+" button / date line.
    key: 'nextAction', label: 'Next action', accessor: (d) => (d.nextActionNotes || '').slice(0, 24), extra: 34,
    xget: (d) => d.nextActionNotes || '',
    xextra: [{ key: 'next_action_date', label: 'Next action date', get: (d) => d._raw?.next_action_date || '' }],
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ width: col.w, padding: '0 10px' }}>
        <NextActionCell note={d.nextActionNotes} date={d._raw?.next_action_date}
          onSave={(payload) => ctx.onNextAction(d, payload)} />
      </div>
    ),
  },
  {
    // Contact sits right after Next action — the two are read together when
    // working the pipeline, and it used to require scrolling past the money
    // columns. Weighted moved out to the far right to make the room: it's
    // derived from Est. fees × stage probability, so it reads fine off-screen.
    key: 'contact', label: 'Contact', accessor: (d) => d.contactName || '', extra: 18,
    xget: (d) => d.contactName || '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', overflow: 'hidden' }}>
        <ContactCell d={d} editable={ctx.editable}
          onSave={(primary_contact_id) => ctx.onSaveCell(d.id, { primary_contact_id })} />
      </div>
    ),
  },
  {
    // Contact details ride the same Contact row (no extra query) and follow
    // the pick: choose a different contact in any of the three cells and all
    // of them swap. Off by default — most tabs read Contact alone.
    key: 'contactEmail', label: 'Contact email', group: 'People', def: false, extra: 18,
    desc: "The shown contact's email — mailto link + copy. Companies with several contacts get a chevron to pick which one.",
    accessor: (d) => d.contactEmail || '',
    xget: (d) => d.contactEmail || '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', overflow: 'hidden' }}>
        <ContactCell d={d} field="email" editable={ctx.editable}
          onSave={(primary_contact_id) => ctx.onSaveCell(d.id, { primary_contact_id })} />
      </div>
    ),
  },
  {
    key: 'contactPhone', label: 'Contact phone', group: 'People', def: false, extra: 18,
    desc: "The shown contact's phone — tel link + copy. Follows the Contact column's pick.",
    accessor: (d) => d.contactPhone || '',
    xget: (d) => d.contactPhone || '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', overflow: 'hidden' }}>
        <ContactCell d={d} field="phone" editable={ctx.editable}
          onSave={(primary_contact_id) => ctx.onSaveCell(d.id, { primary_contact_id })} />
      </div>
    ),
  },
  {
    // Hot score (202609161000): the team's manual 1–10 priority for the deal.
    // Ships ON — it exists so the grid can be sorted and filtered by it while
    // the data is being cleaned up. Click the cell for the ten-step picker.
    // Atlas-only, so no DealCloud lock.
    key: 'hotScore', label: 'Hot score', align: 'right', extra: 30,
    desc: 'Your manual 1–10 priority for this deal. Sort by it, or filter on it (exactly, below, or N and above). Unscored deals never match the filter.',
    accessor: (d) => (d.hotScore == null ? '' : String(d.hotScore)),
    xget: (d) => d.hotScore ?? '',
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', justifyContent: 'flex-end' }} className="num">
        <window.HotScoreCell fill value={d.hotScore} onSave={(hot_score) => ctx.onSaveCell(d.id, { hot_score })} />
      </div>
    ),
  },
  {
    key: 'fees', label: 'Est. fees', align: 'right', accessor: (d) => fmtMoney(d.size),
    xget: (d) => d.size ?? '',
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', fontSize: 12.5, fontWeight: 500, justifyContent: 'flex-end' }} className="num">
        <window.EditableCell type="currency" fill align="right" value={d.size} locked={ctx.dcLockedE(d, 'estimated_fees')} onSave={(estimated_fees) => ctx.onSaveCell(d.id, { estimated_fees })} />
      </div>
    ),
  },
  {
    key: 'rev', label: 'Revenue', align: 'right', accessor: (d) => fmtMoney(d.reportedRevenue ?? d.companyRevenueEst),
    // Same company-estimate fallback as the cell; the source column says which
    // one you got so a script can still tell them apart.
    xget: (d) => d.reportedRevenue ?? d.companyRevenueEst ?? '',
    xextra: [{ key: 'revenue_source', label: 'Revenue source',
      get: (d) => (d.reportedRevenue != null ? 'engagement' : (d.companyRevenueEst != null ? 'company_estimate' : '')) }],
    render: (d, ctx, col) => (
      <EstMoneyCell key={col.key} reported={d.reportedRevenue} companyEst={d.companyRevenueEst} w={col.w}
        editable={ctx.editable} locked={ctx.dcLockedE(d, 'reported_revenue')}
        onSave={(reported_revenue) => ctx.onSaveCell(d.id, { reported_revenue })} />
    ),
  },
  {
    key: 'ebitda', label: 'EBITDA', align: 'right', accessor: (d) => fmtMoney(d.reportedEbitda ?? d.companyEbitdaEst),
    xget: (d) => d.reportedEbitda ?? d.companyEbitdaEst ?? '',
    xextra: [{ key: 'ebitda_source', label: 'EBITDA source',
      get: (d) => (d.reportedEbitda != null ? 'engagement' : (d.companyEbitdaEst != null ? 'company_estimate' : '')) }],
    render: (d, ctx, col) => (
      <EstMoneyCell key={col.key} reported={d.reportedEbitda} companyEst={d.companyEbitdaEst} w={col.w}
        editable={ctx.editable} locked={ctx.dcLockedE(d, 'reported_ebitda')}
        onSave={(reported_ebitda) => ctx.onSaveCell(d.id, { reported_ebitda })} />
    ),
  },
  {
    key: 'sector', label: 'Industry', accessor: (d) => d.sector,
    xget: (d) => (d.sector === '—' ? '' : (d.sector || '')),
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.sector}
      </div>
    ),
  },
  {
    key: 'owner', label: 'Dealmaker', accessor: (d) => d.owner, extra: 26,
    xget: (d) => (d.owner === '—' ? '' : (d.owner || '')),
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', gap: 6 }}>
        <window.EditableCell type="user" fill value={d._raw?.dealmaker_id || null} users={ctx.members} fallbackLabel={d._raw?.dealmaker_name} locked={ctx.dcLockedE(d, 'dealmaker_id')}
          onSave={(dealmaker_id) => window.saveOwnerWithScope({
            kind: 'engagement', field: 'dealmaker_id', value: dealmaker_id, users: ctx.members,
            companyName: d.targetName, hasCompany: !!d.target,
            save: (patch) => ctx.onSaveCell(d.id, patch),
          })} />
      </div>
    ),
  },
  {
    // Deal team (202609151200) — the varying cast around the Dealmaker, so it
    // sits next to it. Editable in place like the Dealmaker cell: same
    // EditableCell, type="users".
    key: 'dealTeam', label: 'Deal team', group: 'People', extra: 26,
    desc: 'Everyone working the deal, per deal. Separate from Dealmaker (the single owner) and from Settings → Teams (the standing org chart).',
    // The cell is an avatar stack, not text, so the header label sets the
    // width — an accessor over the joined names would size the column to the
    // longest roster and never be read.
    accessor: () => '',
    xget: (d) => (d.dealTeamNames || []).join(', '),
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', gap: 6 }}>
        <window.EditableCell type="users" fill value={d.dealTeam || []} users={ctx.members}
          fallbackLabel={d.dealTeamNames || []} editable={ctx.editable}
          onSave={(deal_team) => ctx.onSaveCell(d.id, { deal_team })} />
      </div>
    ),
  },
  {
    key: 'estClose', label: 'Est. close', accessor: (d) => (window.fmtDate ? window.fmtDate(d.estCloseDate) : d.estCloseDate),
    filterKind: 'date',
    desc: 'Red + "overdue" when the date has passed and the deal has not closed; amber inside 10 days.',
    xget: (d) => d.estCloseDate || '',
    render: (d, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', fontSize: 11.5, color: 'var(--text-2)', overflow: 'hidden' }}>
        <window.EditableCell type="date" fill value={d.estCloseDate} editable={ctx.editable}
          locked={ctx.dcLockedE(d, 'estimated_close_date')}
          displayAs={d.estCloseDate
            ? <window.EstCloseValue value={d.estCloseDate} closed={window.dealIsClosed(d, ctx.stageBySlug?.[d.stage])} stacked />
            : undefined}
          onSave={(estimated_close_date) => ctx.onSaveCell(d.id, { estimated_close_date })} />
      </div>
    ),
  },
  {
    key: 'wfees', label: 'Weighted', align: 'right', accessor: (d) => fmtMoney(d.weighted),
    desc: 'Est. fees × the stage probability.',
    xget: (d) => d.weighted ?? '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', textAlign: 'right', fontSize: 12, color: 'var(--text-2)' }} className="num">
        {fmtMoney(d.weighted)}
      </div>
    ),
  },
  {
    key: 'interactions', label: 'Interactions', align: 'right', accessor: (d) => String(d.interactions || 0),
    xget: (d) => d.interactions ?? 0,
    xextra: [{ key: 'last_interaction', label: 'Last interaction', get: (d) => d.lastInteractionAt || '' }],
    render: (d, ctx, col) => (
      <div key={col.key} title={d.lastInteractionAt ? `Last interaction: ${window.fmtDate(d.lastInteractionAt)}` : ''}
        style={{ width: col.w, padding: '0 10px', fontSize: 11.5, color: d.interactions ? 'var(--text-2)' : 'var(--text-4)', textAlign: 'right' }} className="num">
        {d.interactions || 0}
      </div>
    ),
  },
  {
    key: 'latestNote', label: 'Latest note', accessor: (d) => (d.latestNoteTitle || d.latestNotePreview || '').slice(0, 28),
    xget: (d) => [d.latestNoteTitle, d.latestNotePreview].filter(Boolean).join(' — '),
    xextra: [{ key: 'latest_note_at', label: 'Latest note at', get: (d) => d.latestNoteAt || '' }],
    render: (d, ctx, col) => <LatestNoteCell key={col.key} d={d} w={col.w} />,
  },
  {
    key: 'nextMeeting', label: 'Next meeting', accessor: (d) => (d.nextMeetingTitle || '').slice(0, 24),
    filterKind: 'date',
    xget: (d) => d.nextMeetingAt || '',
    xextra: [{ key: 'next_meeting_title', label: 'Next meeting title', get: (d) => d.nextMeetingTitle || '' }],
    render: (d, ctx, col) => <NextMeetingCell key={col.key} d={d} w={col.w} />,
  },
  {
    key: 'created', label: 'Created', accessor: (d) => window.fmtDate(window.recordCreated(d._raw)),
    filterKind: 'date',
    xget: (d) => window.recordCreated(d._raw) || '',
    render: (d, ctx, col) => <window.RecordDateCell key={col.key} row={d._raw} kind="created" w={col.w} />,
  },
  {
    key: 'updated', label: 'Updated', accessor: (d) => d.updated,
    filterKind: 'date',
    xget: (d) => d._raw?.last_stage_update || d._raw?.updated_at || '',
    render: (d, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', fontSize: 11.5, color: 'var(--text-3)' }}>{d.updated}</div>
    ),
  },

  // ── Off by default: everything the API already returns but the grid never
  // had room for. Each is one click away in the Columns menu.
  ...[
    { key: 'type',        label: 'Type',          group: 'Deal',  text: (d) => window.engagementTypeLabel ? window.engagementTypeLabel(d.type) : d.type },
    { key: 'recordId',    label: 'ID',            group: 'Deal',  text: (d) => d.dealcloudId,
      desc: 'The deal’s ID — the number on its page, in #/id/<n> links and in imports.' },
    { key: 'dcStage',     label: 'DealCloud stage', group: 'Deal', text: (d) => d.dcStage,
      desc: 'The verbatim DealCloud stage — the real funnel for buyer shells.' },
    { key: 'onHoldReason', label: 'Hold reason',  group: 'Deal',  text: (d) => d.onHoldReason },
    // Associate is a real engagement column (associate_id) and already
    // editable on the deal page, so edit it here too — same EditableCell as
    // the Dealmaker cell above, `anyone` because the Associate slot is not
    // gated on "can own records". `text` still feeds accessor/xget, so the
    // column fits to the name and CSV export keeps emitting it.
    { key: 'associate',   label: 'Associate',     group: 'People', text: (d) => d.associate, extra: 26,
      render: (d, ctx, col) => (
        <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', gap: 6 }}>
          <window.EditableCell type="user" anyone fill value={d._raw?.associate_id || null} users={ctx.members}
            fallbackLabel={d._raw?.associate_name} editable={ctx.editable} locked={ctx.dcLockedE(d, 'associate_id')}
            onSave={(associate_id) => ctx.onSaveCell(d.id, { associate_id })} />
        </div>
      ) },
    { key: 'buyerCount',  label: 'Interested buyers', group: 'Deal', align: 'right', text: (d) => String(d.buyerCount || 0) },
    { key: 'noteCount',   label: 'Notes',         group: 'Activity', align: 'right', text: (d) => String(d.noteCount || 0) },
    { key: 'liveDate',    label: 'Live since',    group: 'Dates', date: (d) => d.liveDate,
      desc: 'Earliest of the DealCloud live date and the first linked note.' },
    { key: 'closedDate',  label: 'Closed',        group: 'Dates', date: (d) => d.closedDate },
    { key: 'closingProcessDate', label: 'Closing process', group: 'Dates', date: (d) => d.closingProcessDate },
    { key: 'lastActivityAt', label: 'Last activity', group: 'Activity', date: (d) => d.lastActivityAt },
    { key: 'feesInvoiced', label: 'Fees invoiced', group: 'Money', align: 'right', text: (d) => fmtMoney(d.totalFeesInvoiced), raw: (d) => d.totalFeesInvoiced },
    { key: 'feesReceived', label: 'Fees received', group: 'Money', align: 'right', text: (d) => fmtMoney(d.totalFeesReceived), raw: (d) => d.totalFeesReceived },
  ].map((c) => ({
    key: c.key, label: c.label, group: c.group, desc: c.desc, align: c.align, def: false, extra: c.extra,
    filterKind: c.date ? 'date' : undefined,   // right-aligned ones already read as numbers
    accessor: (d) => (c.date ? (window.fmtDate(c.date(d)) || '') : (c.text(d) || '')),
    // Export the underlying value, not the rendered one: ISO dates and bare
    // numbers, matching how the built-in columns export.
    xget: (d) => (c.raw ? (c.raw(d) ?? '') : c.date ? (c.date(d) || '') : (c.text(d) || '')),
    // Most of these are read-only text; one that needs an editor (Associate)
    // brings its own render and keeps the shared accessor/xget above.
    render: c.render || ((d, ctx, col) => {
      const v = c.date ? window.fmtDate(c.date(d)) : c.text(d);
      return (
        <div key={col.key} style={{
          width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
          justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
          fontSize: 11.5, color: v ? 'var(--text-2)' : 'var(--text-4)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} className={c.align === 'right' ? 'num' : ''}>
          {v || '—'}
        </div>
      );
    }),
  })),
];

const dcLockedE = (d, field) => (d._raw?.dc_synced_fields || []).includes(field);

// One row (+ its optional buyers panel), shared between the flat and grouped
// renders. Memoized so a selection toggle, cell save, or new page only
// re-renders the rows whose props actually changed — cellCtx is memoized in
// DealsTable, so the common invalidation is just the edited row object.
const DealRow = React.memo(({ d, checked, isExpanded, buyersState, cols, cellCtx, onOpen, onToggleRow, onOpenCompany, fading }) => (
  <React.Fragment>
    <div onClick={() => onOpen(d.id)}
      style={{
        display: 'flex', alignItems: 'center', minHeight: 38,
        borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        cursor: 'pointer', transition: 'background .1s, opacity .2s',
        opacity: fading ? 0.45 : 1,   // leaving this tab; Undo keeps it (saveMove)
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
    >
      <div style={{ width: 36, padding: '0 10px' }} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox"
          checked={checked}
          onChange={() => onToggleRow && onToggleRow(d.id)}
          style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
      </div>
      {cols.map((c) => c.render(d, cellCtx, c))}
    </div>
    {isExpanded && (
      <div style={{ borderBottom: '1px solid var(--border)' }}>
        <InterestedBuyers state={buyersState} onOpenCompany={onOpenCompany} />
      </div>
    )}
  </React.Fragment>
));

const DealsTable = ({ rows, stageBySlug, stages = [], groupBy = '', sortKey, sortDir, onSort, onOpen, onOpenCompany, members = [], onSaveCell, onStage, fading, onHold, onExit, onWon, onNextAction, selected, onToggleRow, onToggleAll, columns }) => {
  const editable = !!onSaveCell;
  // Stage picker options per engagement type — a buyside deal must not be
  // offered the sell-side family (and vice versa), same rule the detail view
  // and the create drawer use. Cached per type; the map is rebuilt whenever
  // the loaded stage list changes.
  const stageOptsCache = React.useMemo(() => new Map(), [stages]);
  const stageOptsFor = React.useCallback((t) => {
    if (!stageOptsCache.has(t)) {
      stageOptsCache.set(t, (window.stagesForDealType(stages, t) || [])
        .map((s) => ({ value: s.id, label: s.name, hue: STAGE_HUE[s.slug] || 'slate' })));
    }
    return stageOptsCache.get(t);
  }, [stageOptsCache, stages]);
  // Expand-to-buyers state. Seller rows with interested buyers get a chevron;
  // clicking lazy-loads /api/buyers/for-engagement once and caches it, so
  // collapse/re-expand is instant. loadedRef guards against a double-fetch.
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [buyersByEng, setBuyersByEng] = React.useState({});
  const loadedRef = React.useRef({});
  const toggleExpand = React.useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    if (!loadedRef.current[id]) {
      loadedRef.current[id] = true;
      setBuyersByEng((p) => ({ ...p, [id]: { loading: true, items: [] } }));
      window.AtlasApi.buyersForEngagement(id)
        .then((r) => setBuyersByEng((p) => ({ ...p, [id]: { loading: false, items: (r && r.items) || [] } })))
        .catch(() => setBuyersByEng((p) => ({ ...p, [id]: { loading: false, items: [], error: true } })));
    }
  }, []);
  // Grouped rendering: groups recompute from the (already filtered + sorted)
  // rows, so infinite-scroll pages just fold into their sections. Collapse
  // state resets when the grouping dimension changes — stale keys from a
  // previous dimension could silently hide a fresh group that happens to
  // share a key string.
  const groups = React.useMemo(() => buildGroups(rows, groupBy, stageBySlug), [rows, groupBy, stageBySlug]);
  const [collapsedGroups, setCollapsedGroups] = React.useState(() => new Set());
  React.useEffect(() => { setCollapsedGroups(new Set()); }, [groupBy]);
  const toggleGroup = React.useCallback((key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  // Visible columns in the user's order, resolved by useColumnPrefs in the
  // parent view (which also owns the Columns button in the toolbar).
  const cols = columns.cols;
  const startResize = columns.startResize;
  // Everything a catalog cell needs. One object so a cell can't quietly
  // capture a stale closure from an earlier render. Memoized so DealRow's
  // React.memo holds: with stable callbacks from the parent, the only
  // routine invalidations are `expanded` (rare) and the stage/member loads.
  const cellCtx = React.useMemo(() => ({
    editable, onSaveCell, onStage, onHold, onNextAction, onOpenCompany, dcLockedE, stageOptsFor,
    onExit, onWon, members, stages, stageBySlug, expanded, toggleExpand,
  }), [editable, onSaveCell, onStage, onHold, onNextAction, onOpenCompany, stageOptsFor,
    onExit, onWon, members, stages, stageBySlug, expanded, toggleExpand]);

  return (
    <div style={{ minWidth: 'max-content' }}>
      <div style={{
        display: 'flex', alignItems: 'center', height: 30,
        borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
        position: 'sticky', top: 0, zIndex: 1, fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
      }}>
        <div style={{ width: 36, padding: '0 10px' }}>
          <input type="checkbox"
            title="Select all rows (for Export)"
            checked={Boolean(rows.length) && rows.every((d) => selected?.has(d.id))}
            onChange={onToggleAll}
            style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
        </div>
        {cols.map((c) => (
          <window.SortableHeader key={c.key} col={c}
            sortKey={sortKey} sortDir={sortDir}
            onSort={onSort}
            sortable={Boolean(SORT_EXTRACT[c.key])}
            onResizeStart={startResize(c.key)}
          />
        ))}
      </div>

      {(() => {
        const renderRow = (d) => (
          <DealRow key={d.id} d={d}
            checked={Boolean(selected?.has(d.id))}
            isExpanded={expanded.has(d.id)}
            buyersState={buyersByEng[d.id]}
            fading={Boolean(fading && fading.has(d.id))}
            cols={cols} cellCtx={cellCtx}
            onOpen={onOpen} onToggleRow={onToggleRow} onOpenCompany={onOpenCompany} />
        );
        // Flat + nothing expanded = uniform row heights, so the big scopes
        // ("all teams" actives) can window. Grouped or expanded rendering has
        // variable heights and stays a plain map.
        if (!groups) {
          if (expanded.size === 0) return <window.WindowedRows rows={rows} renderRow={renderRow} />;
          return rows.map(renderRow);
        }
        return groups.map((g) => {
          const isCollapsed = collapsedGroups.has(g.key);
          return (
            <React.Fragment key={'g:' + g.key}>
              {/* Section header — content pinned sticky-left so it stays
                  readable when the wide grid is scrolled horizontally.
                  Click anywhere on the band to collapse/expand. */}
              <div onClick={() => toggleGroup(g.key)} style={{
                display: 'flex', alignItems: 'center', height: 32,
                background: 'var(--surface-2)', borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
              }}>
                <div style={{ position: 'sticky', left: 0, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 10px' }}>
                  <span style={{ display: 'inline-flex', color: 'var(--text-3)', transition: 'transform .12s', transform: isCollapsed ? 'none' : 'rotate(90deg)' }}>
                    <Icon.ChevronR size={12} />
                  </span>
                  {g.hue
                    ? <Pill hue={g.hue} dot sz="sm">{g.name}</Pill>
                    : <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{g.name}</span>}
                  <span className="num" style={{ fontSize: 11, color: 'var(--text-3)' }}>{g.items.length}</span>
                  <span className="num" style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>{fmtMoney(g.fees)}</span>
                </div>
              </div>
              {!isCollapsed && g.items.map(renderRow)}
            </React.Fragment>
          );
        });
      })()}
    </div>
  );
};

const DealsBoard = ({ rows, stages, onOpen, total, loaded, hasMore, paging, onLoadMore }) => {
  if (!stages || stages.length === 0) {
    // Skeleton kanban: 5 ghosted columns each with 3 ghosted cards.
    return (
      <div style={{ display: 'flex', gap: 12, padding: 16, overflowX: 'auto' }}>
        {Array.from({ length: 5 }).map((_, ci) => (
          <div key={ci} style={{
            minWidth: 220, padding: 12, borderRadius: 8,
            background: 'var(--surface-2, #f3f4f6)',
            border: '1px solid var(--border, #e5e7eb)',
          }}>
            <Skeleton w={100 + (ci * 17) % 80} h={12} style={{ marginBottom: 12 }} />
            {Array.from({ length: 3 }).map((__, ri) => (
              <div key={ri} style={{
                padding: 8, marginBottom: 6, borderRadius: 6,
                background: 'var(--surface, #fff)',
                border: '1px solid var(--border, #e5e7eb)',
              }}>
                <Skeleton w="80%" h={11} style={{ marginBottom: 6 }} />
                <Skeleton w="55%" h={9} />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  // Group: marketing-category stages first, then dealmaker, with a divider.
  // Buyer columns never apply (shells are excluded server-side on every tab).
  // Sellside columns (082) appear only while a loaded row actually sits on
  // one — most boards are pure buyside and 11 extra empty columns bury them.
  const stagesInUse = new Set(rows.map((d) => d.stage));
  const ordered = [...stages]
    .filter((s) => s.category !== 'buyer')
    .filter((s) => s.category !== 'sellside' || stagesInUse.has(s.slug))
    .sort((a, b) => a.order_index - b.order_index);
  const dealmakerStart = ordered.findIndex((s) => s.category === 'dealmaker');
  const sellsideStart = ordered.findIndex((s) => s.category === 'sellside');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* The board only renders rows already loaded into the list (the table's
          infinite scroll fans pages in). Without this caveat the columns look
          deceptively empty — e.g. "Qualified · 0" while hundreds exist further
          down the unloaded pages. Be explicit and let the user pull more in. */}
      {hasMore && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
          borderBottom: '1px solid var(--border)', background: 'var(--amber-bg)',
          color: 'var(--amber-fg)', fontSize: 11.5, flexShrink: 0,
        }}>
          <span style={{ flex: 1 }}>
            Showing the {loaded.toLocaleString()} deals loaded so far of {total.toLocaleString()}.
            The board only groups loaded rows — load more, or use Table + filters to narrow the set.
          </span>
          <Btn size="xs" variant="outline" onClick={onLoadMore} disabled={paging}>
            {paging ? 'Loading…' : 'Load more'}
          </Btn>
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, padding: 16, flex: 1, overflowX: 'auto', minWidth: 'max-content' }}>
      {ordered.map((stage, i) => {
        const items = rows.filter(d => d.stage === stage.slug && !d.stale);
        const total = items.reduce((s, d) => s + (d.size || 0), 0);
        const hue = STAGE_HUE[stage.slug] || 'slate';
        return (
          <React.Fragment key={stage.slug}>
            {((i === dealmakerStart) || (i === sellsideStart)) && i > 0 && (
              <div style={{ width: 1, background: 'var(--border-strong)', alignSelf: 'stretch' }} />
            )}
            <div style={{ width: 270, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '0 4px 8px' }}>
                <Pill hue={hue} dot sz="sm">{stage.name}</Pill>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }} className="num">{items.length}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }} className="num">{fmtMoney(total)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {items.slice(0, 50).map((d) => {
                  const logoColor = window.hashColor(d.targetName || d.name);
                  return (
                    <button key={d.id} onClick={() => onOpen(d.id)} style={{
                      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9,
                      padding: '10px 11px', textAlign: 'left', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', gap: 7,
                      transition: 'border-color .12s, box-shadow .12s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Logo name={d.targetName || d.name} color={logoColor} size={18} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{d.targetName || '—'}</div>
                        </div>
                      </div>
                      {d.onHold && <Pill hue="amber" sz="sm">On hold · {d.onHold}{d.onHoldReason ? ` · ${prettyLabel(d.onHoldReason)}` : ''}</Pill>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
                        <span className="num" style={{ fontWeight: 500 }}>{fmtMoney(d.size)}</span>
                        <span style={{ color: 'var(--text-4)' }}>·</span>
                        <UserLink id={d._raw?.dealmaker_id} name={d.owner} style={{ color: 'var(--text-3)' }} />
                        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{d.updated}</span>
                      </div>
                    </button>
                  );
                })}
                {items.length > 50 && (
                  <div style={{ fontSize: 10.5, color: 'var(--text-4)', textAlign: 'center', padding: 4 }}>
                    + {items.length - 50} more
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
};

// Shared by most list pages (window.PageHeader). On touch (iPad portrait)
// the action row wraps under the title instead of running off the screen —
// a desktop window keeps the single row it always had.
const PageHeader = ({ breadcrumb, title, meta, actions }) => {
  const wrap = !!window.useDevice().touch;
  return (
    <header style={{ padding: '14px 16px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', marginBottom: 6 }}>
        {breadcrumb.map((b, i) => (
          <React.Fragment key={i}>
            <span>{b}</span>
            {i < breadcrumb.length - 1 && <Icon.ChevronR size={11} color="var(--text-4)" />}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, ...(wrap ? { flexWrap: 'wrap', rowGap: 8 } : null) }}>
        <h1 style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)' }}>{title}</h1>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{meta}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, ...(wrap ? { flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' } : null) }}>{actions}</div>
      </div>
    </header>
  );
};

// `initialCompany` ({id, name}) pre-links a company — used by the quick-create
// buttons on the company detail page. The preset is prepended as a static
// picker option; everything else comes from server-backed search.
// `initialType` opens on a type (the "+ New" menu's "Buyer engagement");
// `initialDeal` / `initialBuyer` ({id, name}) pre-pick the seller deal and
// the buyer firm of a buyer engagement — the page you're on.
// `zIndex` (optional) lifts the drawer over an open reference drawer — the v2
// contact page passes it; the default is the Drawer's own.
const NewEngagementDrawer = ({ stages, activeUser, onClose, onCreated, initialCompany, initialType, initialDeal, initialBuyer, zIndex }) => {
  const [name, setName]         = React.useState(
    initialCompany ? `${initialCompany.name} — Sell-Side` : ''
  );
  const [nameTouched, setNameTouched] = React.useState(false);
  const [companyId, setCompanyId] = React.useState(initialCompany?.id || '');
  const [engType, setEngType]   = React.useState(initialType || 'seller_buyside');
  const [approach, setApproach] = React.useState(null);  // sale_approach checkmarks
  const [stageId, setStageId]   = React.useState('');
  const [dealmakerId, setDealmakerId] = React.useState('');

  // The buyside/sellside process toggle changes which stage family the Stage
  // select offers — a stage picked under the other type would submit a slug
  // its pipeline doesn't contain, so it only survives if it's still in-family.
  const changeType = (v) => {
    setEngType(v);
    setStageId((prev) => (
      prev && window.stagesForDealType(stages, v).some((s) => s.id === prev) ? prev : ''
    ));
  };
  // Approach ↔ type coherence: full sell-side IS the sellside process; the
  // two targeted approaches run the buyside stages.
  const pickApproach = (v) => {
    setApproach(v);
    if (v === 'full_sellside') {
      if (engType !== 'sell_side') changeType('sell_side');
    } else if (v && engType === 'sell_side') {
      changeType('seller_buyside');
    }
  };
  // A number (or null), not a string — MoneyInput parses the shorthand.
  const [fees, setFees]         = React.useState(null);
  const [closeDate, setCloseDate] = React.useState('');
  // Static picker extras only (preset company, just-created company) — the
  // Combobox searches the server for everything else.
  const [companies, setCompanies] = React.useState(
    initialCompany?.id ? [{ value: initialCompany.id, label: initialCompany.name }] : []
  );
  const [members, setMembers]   = React.useState([]);
  const [busy, setBusy]         = React.useState(false);
  const [creatingCompany, setCreatingCompany] = React.useState(false);
  const [industries, setIndustries] = React.useState(null);  // lazy — only if "+ New company" opens
  // Live deals already open at the picked company (GET /engagements/duplicate-check):
  // the Ratti / Ritz duplicates of 2026-09 came from this drawer. Advisory —
  // a second live deal is sometimes right.
  const [liveDeals, setLiveDeals] = React.useState([]);
  React.useEffect(() => {
    if (!companyId) { setLiveDeals([]); return undefined; }
    let alive = true;
    window.AtlasApi.checkEngagementDuplicates({ company_id: companyId, engagement_type: engType })
      .then((r) => { if (alive) setLiveDeals((r && r.live) || []); })
      .catch(() => { if (alive) setLiveDeals([]); });
    return () => { alive = false; };
  }, [companyId, engType]);

  const openCompanyCreate = () => {
    if (industries === null) {
      window.AtlasApi.listIndustries()
        .then((i) => setIndustries(Array.isArray(i) ? i : []))
        .catch(() => setIndustries([]));
    }
    setCreatingCompany(true);
  };

  React.useEffect(() => {
    window.AtlasData.loadTeamMembers()
      .then((m) => {
        const list = Array.isArray(m) ? m : [];
        setMembers(list);
        // Default the dealmaker to the current user if they're on the team.
        if (activeUser?.id && list.some((x) => x.id === activeUser.id)) setDealmakerId(activeUser.id);
      })
      .catch(() => setMembers([]));
  }, [activeUser]);

  // Auto-fill the engagement name from the picked company until the user
  // types their own — "<Company> — Sell-Side" matches the existing naming.
  // The label rides on the Combobox change event (search results aren't in
  // the static `companies` list).
  const onPickCompany = (id, label) => {
    setCompanyId(id);
    if (!nameTouched) {
      const text = label || companies.find((x) => x.value === id)?.label;
      const suffix = engType === 'buyer_buyside' ? 'Buy-Side' : 'Sell-Side';
      setName(id && text ? `${text} — ${suffix}` : '');
    }
  };

  const canSubmit = name.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const eng = await window.AtlasApi.createEngagement({
        name: name.trim(),
        company_id: companyId || null,
        engagement_type: engType,
        sale_approach: approach || null,
        stage_id: stageId || null,
        dealmaker_id: dealmakerId || null,
        estimated_fees: fees || null,
        estimated_close_date: closeDate || null,
      });
      pushToast({ type: 'success', title: 'Engagement created', body: name.trim() });
      onCreated(eng);
    } catch (e) {
      pushToast({ type: 'error', title: 'Create failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  const typeField = (
    <Field label="Type" hint={engType === 'buyer_buyside'
      ? "A buyer engagement is one buyer's pursuit of one of our seller deals — pick the deal and the buyer."
      : 'Sell-side runs the formal sell-side stages; the other seller types run the buyside funnel.'}>
      <Select value={engType} onChange={(e) => {
        const v = e.target.value;
        changeType(v);
        setApproach((a) => (v === 'sell_side' ? 'full_sellside' : (a === 'full_sellside' ? null : a)));
      }}
        options={[
          { value: 'seller_buyside', label: 'Seller (buyside)' },
          { value: 'buyer_buyside',  label: 'Buyer (buyside)' },
          { value: 'sell_side',      label: 'Sell-side' },
          { value: 'other',          label: 'Other' },
        ]} />
    </Field>
  );

  // A buyer engagement isn't a deal of its own: it lives on the seller deal
  // the buyer is buying into, with its NDA and pursuit stage. Created bare
  // here it had neither (Gemspring on Diversified IS, Rosewood on Jack's
  // Lawn Care) — so the Buyer type hands over to the add-a-buyer form.
  if (engType === 'buyer_buyside' && window.AddBuyerToDealDrawer) {
    return (
      <window.AddBuyerToDealDrawer
        typeField={typeField}
        initialDeal={initialDeal}
        initialBuyer={initialBuyer}
        title="New engagement"
        submitLabel="Create buyer engagement"
        onClose={onClose}
        onDone={(out) => {
          onClose();
          if (out?.deal_id && window.AtlasNav) window.AtlasNav.goToDeal(out.deal_id);
        }}
      />
    );
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title="New engagement"
      width={540}
      zIndex={zIndex}
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create engagement'}
          </Btn>
        </>
      )}
    >
      <Field label="Company" hint="Optional — search by name, or create it right here. Picking one names the engagement.">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Combobox value={companyId} onChange={(e) => onPickCompany(e.target.value, e.label)}
              placeholder="Search companies…"
              options={[{ value: '', label: '(none)' }, ...companies]}
              loadOptions={window.searchCompanyOptions} />
          </div>
          <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
            onClick={openCompanyCreate} title="Create a company that isn't in Atlas yet">
            New company
          </Btn>
        </div>
      </Field>
      {liveDeals.length > 0 && (
        <div style={{ margin: '-4px 0 12px', padding: '9px 11px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
                      border: '1px solid var(--amber-border, var(--border))', background: 'var(--amber-bg, var(--surface-2))' }}>
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>
            {liveDeals.length === 1 ? 'This company already has a live deal' : `This company already has ${liveDeals.length} live deals`}
          </div>
          {liveDeals.map((d) => (
            <div key={d.id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', color: 'var(--text-2)' }}>
              <a href={`#/deals/${d.id}`} onClick={(e) => { e.preventDefault(); onClose(); window.AtlasNav && window.AtlasNav.goToDeal(d.id); }}
                style={{ color: 'var(--accent-text)', fontWeight: 500 }}>{d.name}</a>
              <span style={{ color: 'var(--text-3)' }}>
                {[d.stage_name, d.on_hold ? 'on hold' : null, d.dealmaker_name].filter(Boolean).join(' · ')}
              </span>
            </div>
          ))}
          <div style={{ color: 'var(--text-3)', marginTop: 3 }}>Open it instead, or create this one if it's really a second deal.</div>
        </div>
      )}
      <Field label="Engagement name">
        <TextInput value={name}
          onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
          placeholder="Acme — Sell-Side" />
      </Field>
      {typeField}
      {engType !== 'buyer_buyside' && (
        <Field label="Approach" hint="Targeted = buyer pays the fee · Competitive targeted = seller pays, no CIM prep · Full sell-side = the formal process (its own stages).">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 2 }}>
            {(window.SALE_APPROACHES || []).map((a) => (
              <label key={a.value} title={a.hint}
                style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={approach === a.value}
                  onChange={() => pickApproach(approach === a.value ? null : a.value)} />
                {a.label}
              </label>
            ))}
          </div>
        </Field>
      )}
      <Field label="Stage" hint="Optional — leave blank to start unstaged.">
        <Select value={stageId} onChange={(e) => setStageId(e.target.value)}
          options={[
            { value: '', label: '(unstaged)' },
            ...window.stagesForDealType(stages, engType).map((s) => ({ value: s.id, label: s.name })),
          ]} />
      </Field>
      <Field label="Dealmaker">
        <Select value={dealmakerId} onChange={(e) => setDealmakerId(e.target.value)}
          options={[
            { value: '', label: '(unassigned)' },
            ...members.filter(window.isOwnerEligible)
              .map((m) => ({ value: m.id, label: `${m.full_name} (${(m.role || '').replace(/_/g, ' ')})` })),
          ]} />
      </Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Field label="Est. fees (USD)"><MoneyInput value={fees} onChange={setFees} placeholder="250000 or 250k" /></Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Est. close date"><TextInput type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} /></Field>
        </div>
      </div>
      {/* Portaled to body so the stacked drawer isn't nested inside this
          drawer's <Field> label (label click-forwarding + focus traps). */}
      {creatingCompany && ReactDOM.createPortal(
        <window.NewCompanyDrawer
          industries={industries || []}
          members={members}
          onClose={() => setCreatingCompany(false)}
          onCreated={(company) => {
            setCreatingCompany(false);
            if (company?.id) {
              setCompanies((prev) => [{ value: company.id, label: company.name }, ...prev]);
              setCompanyId(company.id);
              // Same auto-naming as onPickCompany (which reads the stale
              // `companies` closure, so name directly from the new row).
              if (!nameTouched) {
                const suffix = engType === 'buyer_buyside' ? 'Buy-Side' : 'Sell-Side';
                setName(`${company.name} — ${suffix}`);
              }
            }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </Drawer>
  );
};

Object.assign(window, { DealsView, PageHeader, NewEngagementDrawer, NextActionCell });
// Shared with the phone list (mobile/deals.jsx) so both select, sort and
// label the same rows the same way.
Object.assign(window, {
  buildEngagementParams,
  ENG_SERVER_SORT_KEYS: SERVER_SORT_KEYS,
  engTabDefaultSort: TAB_DEFAULT_SORT,
  mapEngDeepTab: mapDeepTab,
  EngStageBadge: StageBadge,
  engHoldLabel: holdLabel,
});
